/**
 * United States hops — Yakima, Willamette, Idaho and Michigan grown.
 *
 * Figures follow grower and broker published specifications (Yakima Chief
 * Hops, Hopsteiner, BarthHaas, Crosby Hop Farm, John I. Haas, Indie Hops,
 * Jackson Hop, CLS Farms, Great Lakes Hops / Hop Alliance) as of our knowledge snapshot. Everything is a range
 * because alpha, oil and cohumulone all move with crop year, farm and lot — a
 * single number would be a lie dressed as a spec. Experimental HBC and YQH
 * selections stay under their trial codes until a commercial name sticks.
 *
 * Oil fractions are percentages of total oil, so the four we model do not sum
 * to 100; the balance is linalool, geraniol, selinenes and the rest. Where a
 * grower publishes no credible breakdown the whole `oilComposition` is `null`
 * rather than guessed.
 */

import type { Hop } from '../../schema/hop';

/** US-grown hop varieties. @see Hop */
export const unitedStatesHops: readonly Hop[] = [
  {
    id: 'adeena',
    name: 'Adeena',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 3.5, max: 5 },
    betaAcidPercent: { min: 3, max: 4 },
    cohumulonePercentOfAlpha: { min: 34, max: 38 },
    totalOilMlPer100g: { min: 0.8, max: 1.1 },
    oilComposition: {
      myrcenePercent: 28.4,
      humulenePercent: 38.4,
      caryophyllenePercent: 18.3,
      farnesenePercent: 5.5,
    },
    substitutes: ['sterling', 'liberty', 'crystal', 'santiam'],
    flavorTags: ['herbal', 'spicy', 'floral', 'lemon', 'pine'],
    description:
      'A US-grown noble alternative with the oil profile to back the claim: humulene leads at nearly 40 % and myrcene sits under 30, which is the continental shape rather than the American one. Low alpha and a soft bitterness make it a lager and Kolsch hop from first wort to whirlpool.',
    tags: ['usa', 'noble-style', 'aroma'],
    provenance: 'representative',
  },
  {
    id: 'ahtanum',
    name: 'Ahtanum',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 3.5, max: 6.5 },
    betaAcidPercent: { min: 4.5, max: 6.5 },
    cohumulonePercentOfAlpha: { min: 30, max: 35 },
    totalOilMlPer100g: { min: 0.8, max: 1.6 },
    oilComposition: {
      myrcenePercent: 50,
      humulenePercent: 16,
      caryophyllenePercent: 9,
      farnesenePercent: 0.5,
    },
    substitutes: ['cascade', 'amarillo', 'centennial'],
    flavorTags: ['citrus', 'grapefruit', 'floral', 'earthy', 'pine'],
    description:
      'A softer, more perfumed take on the Cascade idea — grapefruit peel and flowers rather than sharp zest. Low alpha keeps it in the whirlpool and dry hop of pale ales where you want aroma without piling on bitterness.',
    tags: ['usa', 'yakima', 'aroma', 'yakima-chief'],
    url: 'https://yakimachiefranches.com/create/brands/ahtanum',
    provenance: 'spec-sheet',
  },
  {
    id: 'alora',
    name: 'Alora',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 7, max: 10 },
    betaAcidPercent: { min: 3, max: 4 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 0.8, max: 1.5 },
    oilComposition: null,
    substitutes: ['citra', 'azacca', 'mosaic', 'ekuanot'],
    flavorTags: ['peach', 'apricot', 'melon', 'yuzu', 'citrus'],
    description:
      "Hopsteiner's HS17701, and an odd one chemically: selinene makes up over half its oil, where most aroma hops lead on myrcene, and it carries several times the bound 3MH thiol precursor of Cascade. What arrives is candied peach, apricot and yuzu. The four fractions this catalog models are not published for it.",
    tags: ['usa', 'hopsteiner', 'aroma', 'thiol', 'hs17701'],
    url: 'https://info.hopsteiner.com/alora',
    provenance: 'spec-sheet',
  },
  {
    id: 'amarillo',
    name: 'Amarillo',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 8, max: 11 },
    betaAcidPercent: { min: 6, max: 7 },
    cohumulonePercentOfAlpha: { min: 20, max: 24 },
    totalOilMlPer100g: { min: 1.5, max: 1.9 },
    oilComposition: {
      myrcenePercent: 68,
      humulenePercent: 9,
      caryophyllenePercent: 5,
      farnesenePercent: 3,
    },
    substitutes: ['cascade', 'centennial', 'citra'],
    flavorTags: ['orange', 'citrus', 'stone-fruit', 'floral', 'tangerine'],
    description:
      'Orange marmalade and apricot with a flowery lift, and low cohumulone that keeps its bitterness rounded. A signature late and dry-hop variety for American pale ale and IPA, and it plays well as a bridge between piney and tropical hops.',
    tags: ['usa', 'yakima', 'dual-purpose', 'vgxp01', 'proprietary'],
    url: 'https://amarillohop.com/',
    provenance: 'spec-sheet',
  },
  {
    id: 'anchovy',
    name: 'Anchovy',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 12.6, max: 12.6 },
    betaAcidPercent: { min: 3.6, max: 3.6 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.61, max: 1.61 },
    oilComposition: null,
    substitutes: ['citra', 'simcoe', 'strata'],
    flavorTags: ['watermelon', 'raspberry', 'pine'],
    description:
      'Segal Ranch selection 24B-05, sold under a name that undersells it: watermelon and raspberry over pine, with none of the savory note the name implies. One crop year of analysis, so treat the figures as a single lot rather than a variety average.',
    tags: ['usa', 'yakima', 'experimental'],
    provenance: 'representative',
  },
  {
    id: 'apollo',
    name: 'Apollo',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'bittering',
    alphaAcidPercent: { min: 15, max: 20 },
    betaAcidPercent: { min: 6, max: 8 },
    cohumulonePercentOfAlpha: { min: 24, max: 28 },
    totalOilMlPer100g: { min: 1.5, max: 2.5 },
    oilComposition: {
      myrcenePercent: 40,
      humulenePercent: 28,
      caryophyllenePercent: 16,
      farnesenePercent: 0.5,
    },
    /** Measured at 22 °C, not 20 °C. */
    storageStability: {
      alphaLostPercentSixMonths: 15,
      source: 'https://patents.google.com/patent/USPP20200P3/en',
    },
    substitutes: ['columbus', 'zeus', 'summit', 'warrior'],
    flavorTags: ['resinous', 'grapefruit', 'pine', 'spicy'],
    description:
      'A Hopsteiner high-alpha bred to bitter efficiently, with grapefruit rind and resin if you use it late. Moderate cohumulone means the bitterness is firm but not scratchy, so it works as a single-charge kettle hop in big IPAs.',
    tags: ['usa', 'hopsteiner', 'high-alpha', 'bittering', 'proprietary'],
    url: 'https://hopsteiner.us/variety-data-sheets/apollo/',
    provenance: 'spec-sheet',
  },
  {
    id: 'aromahop-oe',
    name: 'Aromahop OE',
    origin: 'Germany',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 0, max: 0 },
    betaAcidPercent: { min: 0, max: 0 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 0, max: 0 },
    oilComposition: null,
    substitutes: [],
    flavorTags: ['herbal', 'clean'],
    description:
      'CO₂ hop extract with the alpha acids and most of the beta removed, leaving the aroma oils. Added late in the boil for hop character in a light-stable beer, or early to knock down foam and lift brewhouse efficiency. Zero alpha is what it is sold as, not a gap in the record.',
    tags: ['germany', 'extract', 'aroma', 'product'],
    kind: 'product',
    defaultForm: 'aroma-extract',
    provenance: 'representative',
  },
  {
    id: 'audacia',
    name: 'Audacia',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 5.5, max: 8 },
    betaAcidPercent: { min: 3, max: 3.5 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.1, max: 1.5 },
    oilComposition: null,
    substitutes: ['strata', 'meridian', 'huell-melon', 'barbe-rouge'],
    flavorTags: ['berry', 'lilac', 'rose', 'lavender', 'floral', 'currant'],
    description:
      'An Indie Hops / Oregon State release, a Strata relative crossed onto Hersbrucker stock: sweet-tangy berry with lilac, rose and lavender. Low alpha for an IPA hop, so it is a blender — pair it with Strata, Simcoe or Citra and the floral-berry note cuts through.',
    tags: ['usa', 'oregon', 'aroma', 'indie-hops', 'osu', 'proprietary'],
    url: 'https://indiehops.com/hops/audacia',
    provenance: 'spec-sheet',
  },
  {
    id: 'azacca',
    name: 'Azacca',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 14, max: 16 },
    betaAcidPercent: { min: 4, max: 5.5 },
    cohumulonePercentOfAlpha: { min: 38, max: 45 },
    totalOilMlPer100g: { min: 1.6, max: 2.5 },
    oilComposition: {
      myrcenePercent: 50,
      humulenePercent: 15,
      caryophyllenePercent: 8,
      farnesenePercent: 0.5,
    },
    substitutes: ['citra', 'mosaic', 'el-dorado'],
    flavorTags: ['mango', 'tropical', 'citrus', 'pineapple', 'pine'],
    description:
      'Loud tropical fruit — mango and pineapple over a pithy citrus base — from the ADHA breeding program. Best used cold-side in hazy and modern IPA; its high cohumulone makes it a poor choice for the bittering charge.',
    tags: ['usa', 'yakima', 'dual-purpose', 'adha', 'adha-483', 'proprietary'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/azaccatm',
    provenance: 'spec-sheet',
  },
  {
    id: 'belma',
    name: 'Belma',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 9, max: 12 },
    betaAcidPercent: { min: 4, max: 8 },
    cohumulonePercentOfAlpha: { min: 17, max: 27 },
    totalOilMlPer100g: { min: 1, max: 2.5 },
    oilComposition: null,
    substitutes: ['el-dorado', 'huell-melon', 'cascade', 'amarillo'],
    flavorTags: ['strawberry', 'melon', 'orange', 'pineapple', 'tropical'],
    description:
      'A Puterbaugh Farms selection out of Toppenish with a clean strawberry-and-melon signature over sweet orange. Low cohumulone keeps the bitterness soft; whirlpool and dry hop it in pale ales, wheat beers and anything that wants fruit without dankness.',
    tags: ['usa', 'yakima', 'aroma', 'puterbaugh', 'proprietary'],
    url: 'https://www.crosbyhops.com/hops/varieties/belma',
    provenance: 'spec-sheet',
  },
  {
    id: 'bergamot',
    name: 'Bergamot',
    origin: 'United States',
    growingRegion: 'Michigan',
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 5, max: 8 },
    betaAcidPercent: { min: 6, max: 8.5 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.4, max: 2.2 },
    oilComposition: null,
    substitutes: ['cashmere', 'mandarina-bavaria', 'citra', 'mosaic'],
    flavorTags: ['orange', 'dank', 'cherry', 'citrus', 'tropical'],
    description:
      'A Great Lakes Hops exclusive with a loud orange-and-dank signature — cherry, lemongrass, juniper and mango on top of the citrus. Oil is high for the alpha, so it belongs in the whirlpool and dry hop of pale ales and hazies rather than as a kettle bittering charge. Oil fractions are unpublished.',
    tags: ['usa', 'michigan', 'great-lakes', 'glh', 'proprietary'],
    url: 'https://www.greatlakeshops.com/bergamottrade.html',
    provenance: 'representative',
  },
  {
    id: 'bravo',
    name: 'Bravo',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'bittering',
    alphaAcidPercent: { min: 14, max: 17 },
    betaAcidPercent: { min: 3, max: 5 },
    cohumulonePercentOfAlpha: { min: 29, max: 34 },
    totalOilMlPer100g: { min: 1.6, max: 2.4 },
    oilComposition: {
      myrcenePercent: 45,
      humulenePercent: 18,
      caryophyllenePercent: 10,
      farnesenePercent: 0.5,
    },
    /** Measured at 22 °C, not 20 °C. */
    storageStability: {
      alphaLostPercentSixMonths: 33,
      source: 'https://patents.google.com/patent/USPP18602P3/en',
    },
    substitutes: ['columbus', 'zeus', 'apollo', 'nugget'],
    flavorTags: ['fruity', 'floral', 'orange', 'resinous'],
    description:
      'A Zeus daughter grown for clean, cost-effective alpha with a surprisingly fruity, faintly floral edge. Sensible workhorse for the 60-minute addition in American ales when you want the aroma hops to do the talking.',
    tags: ['usa', 'yakima', 'high-alpha', 'bittering', 'hopsteiner'],
    url: 'https://hopsteiner.us/variety-data-sheets/bravo/',
    provenance: 'spec-sheet',
  },
  {
    id: 'bru-1',
    name: 'BRU-1',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 12.5, max: 16 },
    betaAcidPercent: { min: 8, max: 10.5 },
    cohumulonePercentOfAlpha: { min: 35, max: 37 },
    totalOilMlPer100g: { min: 1, max: 2 },
    oilComposition: {
      myrcenePercent: 33,
      humulenePercent: 11,
      caryophyllenePercent: 10,
      farnesenePercent: 0.3,
    },
    substitutes: ['el-dorado', 'azacca', 'galaxy', 'citra'],
    flavorTags: ['pineapple', 'pear', 'apple', 'grape', 'tropical'],
    description:
      'An open-pollinated Brulotte Farms selection that reads as freshly cut pineapple on the cold side, with pear, apple and a green-grape edge. High beta acid and a synergistic reputation — it deepens other tropical hops more than it carries a beer alone.',
    tags: ['usa', 'yakima', 'dual-purpose', 'adha', 'brulotte', 'proprietary'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/bru-1tm',
    provenance: 'spec-sheet',
  },
  {
    id: 'cascade',
    name: 'Cascade',
    origin: 'United States',
    growingRegion: 'Yakima Valley',
    cultivar: 'cascade',
    purpose: 'dual',
    alphaAcidPercent: { min: 4.5, max: 9 },
    betaAcidPercent: { min: 4.5, max: 7 },
    cohumulonePercentOfAlpha: { min: 33, max: 40 },
    totalOilMlPer100g: { min: 0.7, max: 1.5 },
    oilComposition: {
      myrcenePercent: 52,
      humulenePercent: 11,
      caryophyllenePercent: 5,
      farnesenePercent: 5,
    },
    storageStability: {
      alphaLostPercentSixMonths: 50,
      source: 'https://morebeer.com/blogs/articles/hop-storage',
    },
    substitutes: ['centennial', 'amarillo', 'ahtanum'],
    flavorTags: ['grapefruit', 'citrus', 'floral', 'spicy'],
    description:
      'The hop that defined American craft aroma: grapefruit and flowers with a light spicy edge. Cheap, forgiving and available everywhere, it is still the reference late hop for pale ale and amber ale.',
    tags: ['usa', 'yakima', 'oregon', 'dual-purpose', 'c-hop', 'classic'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/cascade',
    provenance: 'spec-sheet',
  },
  {
    id: 'calypso',
    name: 'Calypso',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 12, max: 14 },
    betaAcidPercent: { min: 5, max: 6 },
    cohumulonePercentOfAlpha: { min: 40, max: 42 },
    totalOilMlPer100g: { min: 1.6, max: 2.5 },
    oilComposition: {
      myrcenePercent: 48,
      humulenePercent: 15,
      caryophyllenePercent: 8,
      farnesenePercent: 0.5,
    },
    substitutes: ['el-dorado', 'azacca', 'cascade', 'bru-1'],
    flavorTags: ['apple', 'pear', 'tropical', 'stone-fruit', 'earthy'],
    description:
      'A Hopsteiner dual hop with a distinctly orchard profile — pear and apple over tropical fruit — and high cohumulone that makes the bitterness a bit sharp if you lean on it early. Better late and in the whirlpool of pale ales and wheat beers.',
    tags: ['usa', 'hopsteiner', 'dual-purpose', 'proprietary'],
    url: 'https://hopsteiner.us/variety-data-sheets/calypso/',
    provenance: 'spec-sheet',
  },
  {
    id: 'cashmere',
    name: 'Cashmere',
    origin: 'United States',
    growingRegion: 'Yakima Valley',
    cultivar: 'cashmere',
    purpose: 'aroma',
    alphaAcidPercent: { min: 7, max: 9.5 },
    betaAcidPercent: { min: 6.5, max: 8 },
    cohumulonePercentOfAlpha: { min: 20, max: 24 },
    totalOilMlPer100g: { min: 1.2, max: 2.2 },
    oilComposition: {
      myrcenePercent: 45,
      humulenePercent: 18,
      caryophyllenePercent: 9,
      farnesenePercent: 0.5,
    },
    /** Published as 75 % remaining, p. 9. */
    storageStability: {
      alphaLostPercentSixMonths: 25,
      source: 'https://www.usahops.org/cabinet/data/2016_VarietyManual%20(1).pdf',
    },
    substitutes: ['cascade', 'lemondrop', 'citra'],
    flavorTags: ['lemon', 'melon', 'coconut', 'lime', 'herbal'],
    description:
      'A Washington State cross of Cascade and Northern Brewer that lands on lemon-lime and melon with a creamy, faintly coconut finish. Very low cohumulone makes it a smooth whirlpool hop for pale ales, wheat beers and session IPA.',
    tags: ['usa', 'washington', 'aroma', 'wsu'],
    url: 'https://www.crosbyhops.com/hops/varieties/cashmere',
    provenance: 'spec-sheet',
  },
  {
    id: 'cashmere-michigan',
    name: 'Cashmere (Michigan)',
    origin: 'United States',
    growingRegion: 'Michigan',
    cultivar: 'cashmere',
    purpose: 'aroma',
    alphaAcidPercent: { min: 5.5, max: 8 },
    betaAcidPercent: { min: 3.5, max: 6 },
    cohumulonePercentOfAlpha: { min: 20, max: 24 },
    totalOilMlPer100g: { min: 0.4, max: 1 },
    oilComposition: {
      myrcenePercent: 40,
      humulenePercent: 27,
      caryophyllenePercent: 12,
      farnesenePercent: 1,
    },
    substitutes: ['cashmere', 'cascade', 'lemondrop', 'citra'],
    flavorTags: ['lemon', 'lime', 'peach', 'coconut', 'herbal'],
    description:
      'Michigan-grown Cashmere. Same WSU plant as the Washington crop, but Great Lakes lots typically come in at lower alpha and much less total oil, so the lemon-lime-coconut note is quieter. Dose it more generously in the whirlpool if you want the Cashmere character to show.',
    tags: ['usa', 'michigan', 'great-lakes', 'aroma', 'wsu'],
    provenance: 'spec-sheet',
  },
  {
    id: 'celeste',
    name: 'Celeste',
    origin: 'United States',
    growingRegion: 'Oregon',
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 12, max: 12 },
    betaAcidPercent: { min: 4.89, max: 4.89 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.42, max: 1.42 },
    oilComposition: null,
    substitutes: ['strata', 'citra', 'simcoe'],
    flavorTags: ['passionfruit', 'melon', 'pine', 'resinous'],
    description:
      'A West Coast Hop Breeding selection, trialled as WCHB-102, with passionfruit and honeydew over pine resin. Cohumulone is low for its alpha, so it can take the bittering charge as well as the late additions in a West Coast IPA.',
    tags: ['usa', 'oregon', 'modern', 'dual'],
    provenance: 'representative',
  },
  {
    id: 'centennial',
    name: 'Centennial',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 9.5, max: 11.5 },
    betaAcidPercent: { min: 3.5, max: 4.5 },
    cohumulonePercentOfAlpha: { min: 28, max: 30 },
    totalOilMlPer100g: { min: 1.5, max: 2.5 },
    oilComposition: {
      myrcenePercent: 50,
      humulenePercent: 14,
      caryophyllenePercent: 6,
      farnesenePercent: 0.5,
    },
    storageStability: {
      alphaLostPercentSixMonths: 37,
      source: 'https://morebeer.com/blogs/articles/hop-storage',
    },
    substitutes: ['cascade', 'amarillo', 'chinook'],
    flavorTags: ['citrus', 'lemon', 'floral', 'pine', 'grapefruit'],
    description:
      'Often called a super-Cascade: the same citrus and flowers, cleaner and about twice the alpha. Carries a whole American IPA on its own from bittering through dry hop.',
    tags: ['usa', 'yakima', 'dual-purpose', 'c-hop', 'classic'],
    url: 'https://www.crosbyhops.com/hops/varieties/centennial',
    provenance: 'spec-sheet',
  },
  {
    id: 'chinook',
    name: 'Chinook',
    origin: 'United States',
    growingRegion: 'Yakima Valley',
    cultivar: 'chinook',
    purpose: 'dual',
    alphaAcidPercent: { min: 12, max: 14 },
    betaAcidPercent: { min: 3, max: 4 },
    cohumulonePercentOfAlpha: { min: 29, max: 35 },
    totalOilMlPer100g: { min: 1.5, max: 2.5 },
    oilComposition: {
      myrcenePercent: 38,
      humulenePercent: 20,
      caryophyllenePercent: 10,
      farnesenePercent: 0.5,
    },
    storageStability: {
      alphaLostPercentSixMonths: 32,
      source: 'https://morebeer.com/blogs/articles/hop-storage',
    },
    substitutes: ['centennial', 'columbus', 'simcoe', 'nugget'],
    flavorTags: ['pine', 'resinous', 'grapefruit', 'spicy'],
    description:
      'Pine sap and grapefruit pith with a hard, assertive bitterness — the classic West Coast IPA backbone. Excellent for bittering, but use a light hand late unless you want the beer to taste like a forest.',
    tags: ['usa', 'yakima', 'dual-purpose', 'west-coast', 'classic'],
    url: 'https://www.crosbyhops.com/hops/varieties/chinook',
    provenance: 'spec-sheet',
  },
  {
    id: 'chinook-michigan',
    name: 'Chinook (Michigan)',
    origin: 'United States',
    growingRegion: 'Michigan',
    cultivar: 'chinook',
    purpose: 'dual',
    alphaAcidPercent: { min: 12, max: 15 },
    betaAcidPercent: { min: 2.8, max: 4 },
    cohumulonePercentOfAlpha: { min: 24, max: 30 },
    totalOilMlPer100g: { min: 1.5, max: 2.2 },
    oilComposition: {
      myrcenePercent: 32,
      humulenePercent: 16,
      caryophyllenePercent: 1.5,
      farnesenePercent: 0.5,
    },
    substitutes: ['chinook', 'centennial', 'columbus', 'nugget'],
    flavorTags: ['pine', 'grapefruit', 'apricot', 'pineapple', 'resinous'],
    description:
      'Michigan-grown Chinook, including Chinook Cup lots. The pine-and-grapefruit spine is still there, but Michigan terroir pushes apricot and pineapple on top and a slightly lower cohumulone. A fruitier take on the West Coast backbone.',
    tags: ['usa', 'michigan', 'great-lakes', 'dual-purpose', 'west-coast'],
    provenance: 'spec-sheet',
  },
  {
    id: 'citra',
    name: 'Citra',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 11, max: 15 },
    betaAcidPercent: { min: 3.5, max: 4.5 },
    cohumulonePercentOfAlpha: { min: 22, max: 24 },
    totalOilMlPer100g: { min: 1.5, max: 3 },
    oilComposition: {
      myrcenePercent: 65,
      humulenePercent: 11,
      caryophyllenePercent: 6,
      farnesenePercent: 0.5,
    },
    substitutes: ['mosaic', 'el-dorado', 'azacca', 'galaxy', 'krush', 'elani'],
    flavorTags: ['grapefruit', 'passionfruit', 'lime', 'tropical', 'mango'],
    description:
      'The defining modern American aroma hop: grapefruit, passionfruit and lime at intensity, with low cohumulone for a soft bitterness. Enormously biotransformative, so it rewards whirlpool and active-fermentation dry hopping in hazy IPA.',
    tags: ['usa', 'yakima', 'dual-purpose', 'hbc-394', 'proprietary'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/citrar',
    provenance: 'spec-sheet',
  },
  {
    id: 'citra-co2-hop-extract',
    name: 'Citra CO₂ Hop Extract',
    origin: 'United States',
    growingRegion: null,
    // Not `citra`: `cultivar` groups origin *lots* of one genotype, and a
    // resin pressed out of the variety is not another place it was grown. The
    // link a brewer wants is the substitute, which it carries.
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 53.23, max: 53.23 },
    betaAcidPercent: { min: 17.59, max: 17.59 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 0, max: 0 },
    oilComposition: null,
    substitutes: ['citra', 'co2-hop-extract'],
    flavorTags: ['citrus', 'tropical', 'passionfruit'],
    description:
      'Single-variety Citra resin from a low-pressure extraction, which keeps more oil and less alpha than a bittering extract. Meant for the whirlpool, where little of it isomerises and the aroma survives. Its oil is 12.6 % by mass — an extract figure, not the mL per 100 g of cone this catalog stores — so the oil field is left at zero rather than filled with a number in the wrong unit.',
    tags: ['usa', 'extract', 'aroma', 'product', 'citra'],
    kind: 'product',
    defaultForm: 'co2-extract',
    provenance: 'representative',
  },
  {
    id: 'cluster',
    name: 'Cluster',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 5.5, max: 8.5 },
    betaAcidPercent: { min: 4.5, max: 5.5 },
    cohumulonePercentOfAlpha: { min: 36, max: 42 },
    totalOilMlPer100g: { min: 0.4, max: 0.8 },
    oilComposition: {
      myrcenePercent: 50,
      humulenePercent: 16,
      caryophyllenePercent: 7,
      farnesenePercent: 0.5,
    },
    storageStability: {
      alphaLostPercentSixMonths: 17,
      source: 'https://morebeer.com/blogs/articles/hop-storage',
    },
    substitutes: ['galena', 'nugget', 'willamette'],
    flavorTags: ['floral', 'spicy', 'blackcurrant', 'earthy'],
    description:
      'The old American landrace that bittered nearly every US beer before the craft era: floral and faintly blackcurrant, with a plain, sturdy bitterness. Worth keeping around for pre-Prohibition lagers, cream ales and historical recipes.',
    tags: ['usa', 'heritage', 'dual-purpose', 'landrace', 'classic'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/cluster',
    provenance: 'spec-sheet',
  },
  {
    id: 'co2-hop-extract',
    name: 'CO₂ Hop Extract',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'bittering',
    alphaAcidPercent: { min: 50, max: 65 },
    betaAcidPercent: { min: 0, max: 0 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 0, max: 0 },
    oilComposition: null,
    substitutes: ['columbus', 'zeus', 'magnum'],
    flavorTags: ['clean', 'resinous'],
    description:
      "Liquid CO₂ resin, roughly 60 % CTZ with blended aroma varieties, and the cleanest way there is to buy bitterness: no vegetal matter, no trub loss, and a can that states its own alpha. Every can differs, so read the label and enter that figure rather than trusting the 50-65 % band. The maker publishes no beta acid, and an extract's oil is a percentage by mass rather than mL per 100 g of cone, so neither is stated here.",
    tags: ['usa', 'extract', 'bittering', 'product'],
    kind: 'product',
    defaultForm: 'co2-extract',
    provenance: 'representative',
  },
  {
    id: 'columbus',
    name: 'Columbus',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 14, max: 18 },
    betaAcidPercent: { min: 4.5, max: 6 },
    cohumulonePercentOfAlpha: { min: 28, max: 35 },
    totalOilMlPer100g: { min: 2, max: 3.5 },
    oilComposition: {
      myrcenePercent: 40,
      humulenePercent: 15,
      caryophyllenePercent: 9,
      farnesenePercent: 0,
    },
    /** Published as 52 % remaining, p. 9. */
    storageStability: {
      alphaLostPercentSixMonths: 48,
      source: 'https://www.usahops.org/cabinet/data/2016_VarietyManual%20(1).pdf',
    },
    substitutes: ['zeus', 'nugget', 'chinook', 'apollo'],
    flavorTags: ['dank', 'resinous', 'pungent', 'herbal', 'citrus'],
    description:
      'One third of the CTZ trio, and the dankest: black pepper, resin and marijuana-adjacent pungency over high alpha. Doubles as a cheap bittering hop and as the classic dank note in West Coast IPA dry hops. Tomahawk is this same cultivar under Yakima Chief’s trademark, so a recipe that names either means this hop.',
    tags: ['usa', 'yakima', 'ctz', 'tomahawk', 'high-alpha', 'dual-purpose'],
    url: 'https://www.crosbyhops.com/hops/varieties/columbus',
    provenance: 'spec-sheet',
  },
  {
    id: 'comet',
    name: 'Comet',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 9.5, max: 12.5 },
    betaAcidPercent: { min: 3, max: 6 },
    cohumulonePercentOfAlpha: { min: 33, max: 42 },
    totalOilMlPer100g: { min: 1.4, max: 2.5 },
    oilComposition: {
      myrcenePercent: 50,
      humulenePercent: 11,
      caryophyllenePercent: 8,
      farnesenePercent: 0.5,
    },
    substitutes: ['cascade', 'chinook', 'centennial'],
    flavorTags: ['grapefruit', 'citrus', 'grassy', 'dank', 'lemon'],
    description:
      'A 1970s USDA release revived by craft brewers for its distinctive wild, grassy grapefruit character. Sharp and slightly rustic — a good accent hop when an all-tropical dry hop needs some grip.',
    tags: ['usa', 'heritage', 'usda', 'dual-purpose'],
    url: 'https://www.crosbyhops.com/hops/varieties/comet',
    provenance: 'spec-sheet',
  },
  {
    id: 'cryo-pop-original-blend',
    name: 'Cryo Pop Original Blend',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 18, max: 22 },
    betaAcidPercent: { min: 7, max: 11 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 2, max: 4 },
    oilComposition: {
      myrcenePercent: 56,
      humulenePercent: 11.5,
      caryophyllenePercent: 8.5,
      farnesenePercent: 0.2,
    },
    substitutes: ['citra', 'mosaic', 'strata'],
    flavorTags: ['tropical', 'citrus', 'candy', 'intense'],
    description:
      'A Yakima Chief blend of experimental varieties, sold as concentrated lupulin rather than as whole pellets, and bred backwards from a target: the varieties were selected for the aroma the blend makes together. Alpha runs 18-22 % because the lupulin is concentrated, so dose it as a Cryo product and not as a pellet.',
    tags: ['usa', 'blend', 'proprietary', 'lupulin', 'cryo'],
    kind: 'blend',
    defaultForm: 'cryo',
    provenance: 'representative',
  },
  {
    id: 'crystal',
    name: 'Crystal',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 3.5, max: 6 },
    betaAcidPercent: { min: 4.5, max: 6.5 },
    cohumulonePercentOfAlpha: { min: 20, max: 26 },
    totalOilMlPer100g: { min: 0.8, max: 2.1 },
    oilComposition: {
      myrcenePercent: 50,
      humulenePercent: 22,
      caryophyllenePercent: 6,
      farnesenePercent: 0.5,
    },
    storageStability: {
      alphaLostPercentSixMonths: 49,
      source: 'https://morebeer.com/blogs/articles/hop-storage',
    },
    substitutes: ['mount-hood', 'liberty', 'hallertauer-mittelfrueh', 'santiam'],
    flavorTags: ['floral', 'spicy', 'woody', 'herbal', 'noble'],
    description:
      'An Oregon triploid Hallertau descendant: mild, woody and spicy with a clean floral top. The default domestic stand-in for noble hops in pilsner, kolsch and altbier when German hops are dear.',
    tags: ['usa', 'oregon', 'aroma', 'noble-type', 'hallertau-derived'],
    url: 'https://www.crosbyhops.com/hops/varieties/crystal',
    provenance: 'spec-sheet',
  },
  {
    id: 'd5-055',
    name: 'Experimental D5-055',
    origin: 'United States',
    growingRegion: 'Yakima Valley',
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 13.4, max: 13.4 },
    betaAcidPercent: { min: 5.7, max: 5.7 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.45, max: 1.45 },
    oilComposition: null,
    substitutes: ['simcoe', 'citra', 'mosaic'],
    flavorTags: ['pomelo', 'pineapple', 'grapefruit'],
    description:
      'An unnamed Segal Ranch selection carrying pomelo, pineapple and ruby grapefruit at a solid 13 % alpha. Figures are one crop year off one ranch, which is what a trial code means.',
    tags: ['usa', 'yakima', 'experimental', 'segal-ranch'],
    provenance: 'representative',
  },
  {
    id: 'dolcita',
    name: 'Dolcita',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 7.5, max: 13 },
    betaAcidPercent: { min: 7, max: 12 },
    cohumulonePercentOfAlpha: { min: 22, max: 24 },
    totalOilMlPer100g: { min: 1, max: 2.7 },
    oilComposition: {
      myrcenePercent: 39,
      humulenePercent: 11,
      caryophyllenePercent: 7,
      farnesenePercent: 0.4,
    },
    substitutes: ['sabro', 'krush', 'el-dorado', 'galaxy'],
    flavorTags: ['peach', 'pineapple', 'coconut', 'orange', 'candy', 'tropical'],
    description:
      'HBC 1019, now sold as Dolcita: peach rings, pineapple daiquiri, coconut and Valencia orange over a creamy caramel note. Unusually high beta for an aroma hop. Still young in the trade — treat the published bands as a crop-year snapshot and keep it late.',
    tags: ['usa', 'yakima', 'aroma', 'hbc-1019', 'proprietary', 'experimental'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/dolcita-hops',
    provenance: 'spec-sheet',
  },
  {
    id: 'ekuanot',
    name: 'Ekuanot',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 13, max: 15.5 },
    betaAcidPercent: { min: 4.5, max: 5.5 },
    cohumulonePercentOfAlpha: { min: 30, max: 37 },
    totalOilMlPer100g: { min: 2.5, max: 4.5 },
    oilComposition: {
      myrcenePercent: 50,
      humulenePercent: 15,
      caryophyllenePercent: 9,
      farnesenePercent: 0.5,
    },
    substitutes: ['citra', 'mosaic', 'simcoe'],
    flavorTags: ['melon', 'papaya', 'lime', 'green-pepper', 'tropical'],
    description:
      'Huge oil loading and an unusual profile — honeydew and papaya cut with lime and a green capsicum note. Sold as Equinox before 2016; superb in the whirlpool of tropical IPA, and easy to overdo.',
    tags: ['usa', 'yakima', 'dual-purpose', 'hbc-366', 'equinox', 'proprietary'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/ekuanotr',
    provenance: 'spec-sheet',
  },
  {
    id: 'elani',
    name: 'Elani',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 9, max: 11 },
    betaAcidPercent: { min: 4, max: 7 },
    cohumulonePercentOfAlpha: { min: 20, max: 35 },
    totalOilMlPer100g: { min: 1.3, max: 2.4 },
    oilComposition: {
      myrcenePercent: 54,
      humulenePercent: 10,
      caryophyllenePercent: 5,
      farnesenePercent: 0.5,
    },
    substitutes: ['citra', 'mosaic', 'simcoe', 'krush'],
    flavorTags: ['orange', 'tangerine', 'pineapple', 'guava', 'peach', 'lime'],
    description:
      'A Yakima Quality Hops release, trialled as YQH-1320, found feral on the St. Joe River in Idaho. Clean orange zest, pineapple and white peach without the onion-garlic thiol note some high-oil hops carry. Works as a single late hop or as the citrus lift in a Citra/Mosaic blend.',
    tags: ['usa', 'idaho', 'aroma', 'yqh-1320', 'yakima-quality-hops', 'proprietary'],
    url: 'https://www.crosbyhops.com/hops/varieties/elani',
    provenance: 'spec-sheet',
  },
  {
    id: 'el-dorado',
    name: 'El Dorado',
    origin: 'United States',
    growingRegion: 'Yakima Valley',
    cultivar: 'el-dorado',
    purpose: 'dual',
    alphaAcidPercent: { min: 13, max: 17 },
    betaAcidPercent: { min: 7, max: 8 },
    cohumulonePercentOfAlpha: { min: 28, max: 33 },
    totalOilMlPer100g: { min: 2.5, max: 3.5 },
    oilComposition: {
      myrcenePercent: 57,
      humulenePercent: 12,
      caryophyllenePercent: 7,
      farnesenePercent: 0.5,
    },
    substitutes: ['citra', 'azacca', 'mosaic'],
    flavorTags: ['stone-fruit', 'pear', 'candy', 'watermelon', 'tropical'],
    description:
      'Sweet stone fruit and pear drop candy, sometimes read as watermelon, from CLS Farms in Yakima. High alpha and high oil let it bitter and dry hop the same beer; excellent in juicy pale ales and fruited sours.',
    tags: ['usa', 'yakima', 'dual-purpose', 'cls-farms', 'proprietary'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/el-dorado',
    provenance: 'spec-sheet',
  },
  {
    id: 'el-dorado-michigan',
    name: 'El Dorado (Michigan)',
    origin: 'United States',
    growingRegion: 'Michigan',
    cultivar: 'el-dorado',
    purpose: 'dual',
    alphaAcidPercent: { min: 12, max: 16 },
    betaAcidPercent: { min: 6.5, max: 8.5 },
    cohumulonePercentOfAlpha: { min: 28, max: 33 },
    totalOilMlPer100g: { min: 2, max: 3.5 },
    oilComposition: null,
    substitutes: ['el-dorado', 'citra', 'azacca', 'mosaic'],
    flavorTags: ['pineapple', 'mango', 'pear', 'watermelon', 'tropical'],
    description:
      'Michigan-grown El Dorado. Same CLS Farms cultivar as the Yakima crop — pineapple, mango, pear and candy — grown on Great Lakes acreage. Published oil breakdowns for Michigan lots are thin, so the chemistry here is class-typical; trust the bag analysis for bittering.',
    tags: ['usa', 'michigan', 'great-lakes', 'dual-purpose', 'cls-farms'],
    provenance: 'representative',
  },
  {
    id: 'emerald-spire',
    name: 'Emerald Spire',
    origin: 'United States',
    growingRegion: 'Michigan',
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 6.5, max: 9 },
    betaAcidPercent: { min: 2.8, max: 4.2 },
    cohumulonePercentOfAlpha: { min: 32, max: 40 },
    totalOilMlPer100g: { min: 0.7, max: 1.3 },
    oilComposition: {
      myrcenePercent: 19,
      humulenePercent: 18,
      caryophyllenePercent: 10,
      farnesenePercent: 0.4,
    },
    substitutes: ['nelson-sauvin', 'hallertau-blanc', 'mosaic', 'paradigm'],
    flavorTags: ['white-grape', 'peach', 'watermelon', 'candy', 'melon'],
    description:
      'A Great Lakes Hops selection that reads as white grape and peach candy with a dry, champagne-like finish. Best late or in the dry hop — early boil charges turn herbal. Pair it with Nelson Sauvin or Hallertau Blanc when you want a white-wine edge on a pale ale.',
    tags: ['usa', 'michigan', 'great-lakes', 'glh', 'proprietary'],
    url: 'https://www.greatlakeshops.com/emerald-spiretrade.html',
    provenance: 'spec-sheet',
  },
  {
    id: 'erebus',
    name: 'Erebus',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 9.7, max: 9.7 },
    betaAcidPercent: { min: 3, max: 4 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 2.3, max: 2.8 },
    oilComposition: null,
    substitutes: ['citra', 'azacca', 'ekuanot'],
    flavorTags: ['blueberry', 'citrus', 'candied-fruit', 'floral'],
    description:
      "Hopsteiner's HS16660, and an unusual aroma for an American hop: blueberry and candied fruit with floral rose behind them, rather than the citrus-pine default. Nearly 3 mL of oil, so it rewards a heavy late hand.",
    tags: ['usa', 'hopsteiner', 'modern'],
    provenance: 'representative',
  },
  {
    id: 'euphorics',
    name: 'Euphorics',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 0, max: 0 },
    betaAcidPercent: { min: 0, max: 0 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 0, max: 0 },
    oilComposition: null,
    substitutes: [],
    flavorTags: ['floral', 'fruity', 'botanical'],
    description:
      'A water-soluble flavor and aroma liquid made from hops and botanicals, added cold-side after fermentation or straight into finished beer. Not purely a hop product, and it carries no alpha acid, so it contributes no bitterness.',
    tags: ['usa', 'extract', 'aroma', 'product', 'botanical'],
    kind: 'product',
    defaultForm: 'aroma-extract',
    provenance: 'representative',
  },
  {
    id: 'eureka',
    name: 'Eureka',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 17, max: 20 },
    betaAcidPercent: { min: 4.5, max: 6 },
    cohumulonePercentOfAlpha: { min: 28, max: 30 },
    totalOilMlPer100g: { min: 2.5, max: 4.5 },
    oilComposition: {
      myrcenePercent: 42,
      humulenePercent: 29,
      caryophyllenePercent: 13,
      farnesenePercent: 0.5,
    },
    substitutes: ['columbus', 'simcoe', 'apollo', 'chinook'],
    flavorTags: ['dark-fruit', 'citrus', 'pine', 'mint', 'resinous', 'dank'],
    description:
      'A Hopsteiner high-alpha with enough oil to double as an aroma hop: blackcurrant and citrus over pine, mint and a dank resinous base. Efficient in the kettle and loud late — a West Coast IPA backbone when you want one variety to do both jobs.',
    tags: ['usa', 'hopsteiner', 'high-alpha', 'dual-purpose', 'proprietary'],
    url: 'https://hopsteiner.us/variety-data-sheets/eureka/',
    provenance: 'spec-sheet',
  },
  {
    id: 'falconers-flight',
    name: "Falconer's Flight",
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 9.5, max: 11.5 },
    betaAcidPercent: { min: 4, max: 6 },
    cohumulonePercentOfAlpha: { min: 25, max: 30 },
    totalOilMlPer100g: { min: 1, max: 2 },
    oilComposition: {
      myrcenePercent: 47.5,
      humulenePercent: 17.5,
      caryophyllenePercent: 8,
      farnesenePercent: 1.75,
    },
    substitutes: ['cascade', 'centennial', 'simcoe', 'amarillo', 'zythos'],
    flavorTags: ['citrus', 'stone-fruit', 'tropical', 'herbal', 'woody'],
    description:
      'Blended by Hopunion in 2010 and carried on by Yakima Chief, from Pacific Northwest aroma varieties the seller does not name — a proprietary recipe that can move between crops, which is why the analysis is a typical band rather than a specification. Citrus, stone fruit and tropical over a woody, herbal base, and the default single-hop shortcut to a West Coast pale ale. Sales fund brewing scholarships through the Glen Hay Falconer Foundation, which is what the name is for.',
    tags: ['usa', 'blend', 'proprietary', 'charity', 'pacific-northwest'],
    kind: 'blend',
    provenance: 'representative',
  },
  {
    id: 'falconers-flight-7cs',
    name: "Falconer's Flight 7 C's",
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 9.5, max: 11.5 },
    betaAcidPercent: { min: 4.5, max: 6 },
    cohumulonePercentOfAlpha: { min: 24, max: 30 },
    totalOilMlPer100g: { min: 1, max: 2.5 },
    oilComposition: {
      myrcenePercent: 42.5,
      humulenePercent: 17,
      caryophyllenePercent: 8.5,
      farnesenePercent: 1.3,
    },
    substitutes: ['cascade', 'centennial', 'chinook', 'columbus', 'crystal'],
    flavorTags: ['citrus', 'berry', 'herbal', 'woody', 'spicy'],
    description:
      'The all-C sibling: seven C-named varieties plus experimental lots, in proportions Yakima Chief keep to themselves. It lands on the same alpha band as the original with more berry and a spicy, earthy floor under the citrus, which suits an American amber or a brown as readily as a pale. Like any vendor blend the recipe can change between crops, so the figures belong to a typical lot rather than to the name.',
    tags: ['usa', 'blend', 'proprietary', 'pacific-northwest'],
    kind: 'blend',
    provenance: 'representative',
  },
  {
    id: 'galena',
    name: 'Galena',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'bittering',
    alphaAcidPercent: { min: 11, max: 14 },
    betaAcidPercent: { min: 7, max: 9 },
    cohumulonePercentOfAlpha: { min: 36, max: 40 },
    totalOilMlPer100g: { min: 0.9, max: 1.3 },
    oilComposition: {
      myrcenePercent: 57,
      humulenePercent: 10,
      caryophyllenePercent: 4,
      farnesenePercent: 0.5,
    },
    storageStability: {
      alphaLostPercentSixMonths: 15,
      source: 'https://morebeer.com/blogs/articles/hop-storage',
    },
    substitutes: ['nugget', 'cluster', 'columbus'],
    flavorTags: ['blackcurrant', 'citrus', 'resinous', 'stone-fruit'],
    description:
      'An Idaho Cluster seedling that was the American bittering standard for decades, giving blackcurrant and a hint of grapefruit. High beta acid means the bitterness holds up well through a long boil and a long cellar.',
    tags: ['usa', 'idaho', 'bittering', 'high-alpha', 'classic'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/galena',
    provenance: 'spec-sheet',
  },
  {
    id: 'glacier',
    name: 'Glacier',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 5, max: 9 },
    betaAcidPercent: { min: 5.5, max: 8.5 },
    cohumulonePercentOfAlpha: { min: 11, max: 13 },
    totalOilMlPer100g: { min: 0.7, max: 1.6 },
    oilComposition: {
      myrcenePercent: 47,
      humulenePercent: 30,
      caryophyllenePercent: 8,
      farnesenePercent: 0.5,
    },
    substitutes: ['willamette', 'fuggle', 'styrian-goldings'],
    flavorTags: ['earthy', 'woody', 'pear', 'herbal', 'blackcurrant'],
    description:
      'A Washington State release with remarkably low cohumulone, giving a very smooth bitterness under a mild woody, pear-skin aroma. A useful domestic replacement for English aroma hops in bitters, browns and porters.',
    tags: ['usa', 'washington', 'aroma', 'wsu', 'english-type'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/glacier',
    provenance: 'spec-sheet',
  },
  {
    id: 'hbc-1134',
    name: 'HBC 1134',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 9.8, max: 12.5 },
    betaAcidPercent: { min: 3, max: 4.5 },
    cohumulonePercentOfAlpha: { min: 20, max: 30 },
    totalOilMlPer100g: { min: 1.8, max: 3 },
    oilComposition: null,
    substitutes: ['hbc-630', 'hbc-638', 'sterling', 'crystal'],
    flavorTags: ['rose', 'pine', 'herbal', 'lemon', 'grapefruit'],
    description:
      'A Hop Breeding Company selection aimed squarely at modern lager: rose and herbal lemon over pine, reading as an American answer to noble character rather than an imitation of it. Low cohumulone for its alpha, which is the point.',
    tags: ['usa', 'experimental', 'hop-breeding-company', 'lager'],
    provenance: 'representative',
  },
  {
    id: 'hbc-522',
    name: 'HBC 522',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 9.5, max: 12 },
    betaAcidPercent: { min: 4.5, max: 5.5 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.4, max: 2.3 },
    oilComposition: null,
    substitutes: ['columbus', 'simcoe', 'chinook'],
    flavorTags: ['jasmine', 'bitter-orange', 'grapefruit', 'pine'],
    description:
      'Columbus crossed onto an experimental male, and it shows: jasmine and bitter orange sit over the pine-needle resin the parent is known for. Enough alpha to bitter with, enough oil to finish with.',
    tags: ['usa', 'experimental', 'hop-breeding-company'],
    provenance: 'representative',
  },
  {
    id: 'hbc-630',
    name: 'HBC 630',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 13, max: 14 },
    betaAcidPercent: { min: 5.5, max: 6.5 },
    cohumulonePercentOfAlpha: { min: 23, max: 26 },
    totalOilMlPer100g: { min: 2.3, max: 3 },
    oilComposition: {
      myrcenePercent: 45,
      humulenePercent: 18,
      caryophyllenePercent: 13,
      farnesenePercent: 0.5,
    },
    substitutes: ['azacca', 'strata', 'barbe-rouge', 'krush'],
    flavorTags: ['cherry', 'raspberry', 'peach', 'banana', 'candy', 'stone-fruit'],
    description:
      'An unreleased Hop Breeding Company selection with a cherry-candy and raspberry profile, plus creamy peach and banana lactones. High oil and moderate cohumulone; still experimental, so availability is lot-by-lot and the name may change if it commercialises.',
    tags: ['usa', 'yakima', 'dual-purpose', 'hbc', 'experimental', 'proprietary'],
    provenance: 'spec-sheet',
  },
  {
    id: 'hbc-638',
    name: 'HBC 638',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 12, max: 16 },
    betaAcidPercent: { min: 3, max: 6 },
    cohumulonePercentOfAlpha: { min: 28, max: 31 },
    totalOilMlPer100g: { min: 1.5, max: 3 },
    oilComposition: {
      myrcenePercent: 36,
      humulenePercent: 17,
      caryophyllenePercent: 13,
      farnesenePercent: 0.5,
    },
    substitutes: ['centennial', 'citra', 'elani', 'cascade'],
    flavorTags: ['citrus', 'floral', 'mango', 'pineapple', 'cherry', 'strawberry'],
    description:
      'A Hop Breeding Company trial hop pitched as a modern Centennial: bright citrus and flowers plus mango, pineapple, cherry and melon. Familiar enough to drop into a classic American IPA, loud enough for a hazy dry hop. Still experimental.',
    tags: ['usa', 'yakima', 'dual-purpose', 'hbc', 'experimental', 'proprietary'],
    provenance: 'spec-sheet',
  },
  {
    id: 'hbc-682',
    name: 'HBC 682',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'bittering',
    alphaAcidPercent: { min: 17, max: 20 },
    betaAcidPercent: { min: 4.5, max: 6 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1, max: 2.5 },
    oilComposition: {
      myrcenePercent: 50,
      humulenePercent: 14,
      caryophyllenePercent: 5,
      farnesenePercent: 0.5,
    },
    substitutes: ['warrior', 'magnum', 'apollo', 'herkules'],
    flavorTags: ['herbal', 'earthy', 'floral', 'woody', 'clean'],
    description:
      'The Hop Breeding Company’s super-alpha, briefly sold as Pahto and now back under its trial code. Clean, non-lingering bitterness with only a mild herbal-earthy aroma — a kettle hop, not a dry hop. High yield and storage-stable, which is why it exists.',
    tags: ['usa', 'yakima', 'bittering', 'high-alpha', 'hbc', 'pahto', 'proprietary'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/terrafluxtm-hops',
    provenance: 'spec-sheet',
  },
  {
    id: 'helios',
    name: 'Helios',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'bittering',
    alphaAcidPercent: { min: 16, max: 21 },
    betaAcidPercent: { min: 4.5, max: 6.5 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.5, max: 2.8 },
    oilComposition: null,
    substitutes: ['hbc-682', 'warrior', 'herkules', 'magnum'],
    flavorTags: ['resinous', 'spicy', 'floral', 'clean'],
    description:
      'Hopsteiner’s climate-bred super-alpha (HS15619): very high alpha, mildew-resistant and grown for yield as much as for beer. Pleasant, slightly spicy-floral bitterness that stays out of the way of the aroma hops. Use it at 60 minutes and forget it.',
    tags: ['usa', 'hopsteiner', 'high-alpha', 'bittering', 'hs15619', 'proprietary'],
    url: 'https://hopsteiner.us/variety-data-sheets/helios/',
    provenance: 'representative',
  },
  {
    id: 'hop-hash',
    name: 'Hop Hash',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 0, max: 0 },
    betaAcidPercent: { min: 0, max: 0 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 0, max: 0 },
    oilComposition: null,
    substitutes: [],
    flavorTags: ['resinous', 'sticky', 'intense'],
    description:
      'The resin scraped from the pelletiser, sold per variety and ranging from fine powder to sticky clumps. Its seller does not test it for any brewing value, so the alpha here is zero rather than invented — which is also the right answer in practice, because hash is a cold-side dry-hop addition and is not meant to bitter. Do not plan a bittering charge around it.',
    tags: ['usa', 'lupulin', 'product', 'untested'],
    kind: 'product',
    defaultForm: 'cryo',
    provenance: 'representative',
  },
  {
    id: 'hophaze',
    name: 'HopHaze',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 0, max: 0 },
    betaAcidPercent: { min: 0, max: 0 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 0, max: 0 },
    oilComposition: null,
    substitutes: [],
    flavorTags: ['neutral'],
    description:
      'A hop-derived liquid dosed for stable haze rather than for flavor, at around 500 mg per liter of beer. It is roughly 10 % hop resin components, which is not an alpha figure and does not bitter — this row adds no IBU.',
    tags: ['usa', 'extract', 'product', 'haze'],
    kind: 'product',
    defaultForm: 'aroma-extract',
    provenance: 'representative',
  },
  {
    id: 'horizon',
    name: 'Horizon',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 10, max: 16 },
    betaAcidPercent: { min: 6.5, max: 8.5 },
    cohumulonePercentOfAlpha: { min: 16, max: 19 },
    totalOilMlPer100g: { min: 1.5, max: 2 },
    oilComposition: {
      myrcenePercent: 62,
      humulenePercent: 9,
      caryophyllenePercent: 7,
      farnesenePercent: 3,
    },
    substitutes: ['magnum', 'nugget', 'warrior', 'glacier'],
    flavorTags: ['floral', 'citrus', 'spicy', 'clean'],
    description:
      'A USDA high-alpha with one of the lowest cohumulone figures in the American book, so the bitterness is unusually smooth for the alpha. Floral and lightly citrus if you add it late; mostly bought as a clean dual hop for pale ales and lagers.',
    tags: ['usa', 'usda', 'dual-purpose', 'high-alpha'],
    url: 'https://hopsteiner.us/variety-data-sheets/horizon/',
    provenance: 'spec-sheet',
  },
  {
    id: 'hydra',
    name: 'Hydra',
    origin: 'United States',
    growingRegion: 'Michigan',
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 7, max: 10 },
    betaAcidPercent: { min: 5, max: 7.5 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.1, max: 1.8 },
    oilComposition: null,
    substitutes: ['mandarina-bavaria', 'cashmere', 'citra', 'mosaic'],
    flavorTags: ['citrus', 'tangerine', 'grape', 'orange', 'mandarin'],
    description:
      'A Great Lakes Hops citrus hop: tangerine, mandarin, clementine and green grape over orange rind. Dual-purpose on paper, but the interesting part is the late addition in pale ales, wheat beers and hoppy lagers. Oil fractions are unpublished.',
    tags: ['usa', 'michigan', 'great-lakes', 'glh', 'proprietary'],
    url: 'https://www.greatlakeshops.com/hydratrade.html',
    provenance: 'representative',
  },
  {
    id: 'idaho-7',
    name: 'Idaho 7',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 12, max: 15 },
    betaAcidPercent: { min: 4, max: 5.5 },
    cohumulonePercentOfAlpha: { min: 28, max: 34 },
    totalOilMlPer100g: { min: 2, max: 3 },
    oilComposition: null,
    substitutes: ['mosaic', 'simcoe', 'citra'],
    flavorTags: ['tropical', 'apricot', 'pine', 'black-tea', 'orange'],
    description:
      'A Jackson Hop selection with an unusual split personality: apricot and orange up front, resinous pine and black tea underneath. Very good in the whirlpool of hazy IPA, where the tea note adds welcome structure.',
    tags: ['usa', 'idaho', 'dual-purpose', 'jackson-hop', 'proprietary'],
    url: 'https://yakimachiefranches.com/create/brands/idaho7',
    provenance: 'spec-sheet',
  },
  {
    id: 'idaho-gem',
    name: 'Idaho Gem',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 11, max: 14 },
    betaAcidPercent: { min: 5, max: 7 },
    cohumulonePercentOfAlpha: { min: 40, max: 45 },
    totalOilMlPer100g: { min: 1.3, max: 2 },
    oilComposition: null,
    substitutes: ['idaho-7', 'mosaic', 'el-dorado', 'citra'],
    flavorTags: ['cherry', 'candy', 'grapefruit', 'floral', 'tropical'],
    description:
      'A Jackson Hop sibling to Idaho 7 that leans candy-cherry and grapefruit rather than apricot and tea. High cohumulone, so keep the bittering charge modest and put the rest in the whirlpool; a useful fruit-candy note in hazy pale ales.',
    tags: ['usa', 'idaho', 'dual-purpose', 'jackson-hop', 'proprietary'],
    url: 'https://mill95hops.com/idaho-gem-brand-us-2',
    provenance: 'spec-sheet',
  },
  {
    id: 'incognito',
    name: 'INCOGNITO',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 0, max: 0 },
    betaAcidPercent: { min: 0, max: 0 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 0, max: 0 },
    oilComposition: null,
    substitutes: [],
    flavorTags: ['tropical', 'citrus', 'resinous'],
    description:
      'A flowable hop extract for the whirlpool that replaces a large pellet charge without the trub loss: 1 g/L stands in for about 5.8 g/L of pellets, and 2 g/L is the ceiling the maker suggests. It carries flavor rather than bitterness, so it contributes no IBU here.',
    tags: ['usa', 'extract', 'aroma', 'product', 'whirlpool'],
    kind: 'product',
    defaultForm: 'aroma-extract',
    provenance: 'representative',
  },
  {
    id: 'isohop',
    name: 'IsoHop',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'bittering',
    alphaAcidPercent: { min: 0, max: 0 },
    betaAcidPercent: { min: 0, max: 0 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 0, max: 0 },
    oilComposition: null,
    isoAlphaPercent: { min: 30, max: 30 },
    substitutes: [],
    flavorTags: ['clean', 'bitter'],
    description:
      'An aqueous solution of pure iso-α-acids from CO₂ extract, standardized at 30 %, added after fermentation to correct a beer that came out under-bittered. Alpha acid is zero and that is not a gap: the isomerisation already happened in a factory, so there is nothing left to convert in a kettle and boil time does not apply to it. Bitterness is figured from the iso-alpha directly.',
    tags: ['usa', 'extract', 'iso-alpha', 'bittering', 'product'],
    kind: 'product',
    defaultForm: 'iso-extract',
    provenance: 'representative',
  },
  {
    id: 'jarrylo',
    name: 'Jarrylo',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 13, max: 16 },
    betaAcidPercent: { min: 6, max: 8 },
    cohumulonePercentOfAlpha: { min: 25, max: 33 },
    totalOilMlPer100g: { min: 1.6, max: 2.5 },
    oilComposition: null,
    substitutes: ['azacca', 'el-dorado', 'cascade'],
    flavorTags: ['banana', 'pear', 'spicy', 'stone-fruit', 'orange'],
    description:
      'An ADHA variety with a distinctly estery aroma — banana and pear with a peppery spice tail. Odd in a straight IPA, excellent in Belgian ales and saisons where it doubles down on the yeast character.',
    tags: ['usa', 'yakima', 'dual-purpose', 'adha', 'proprietary'],
    provenance: 'spec-sheet',
  },
  {
    id: 'krush',
    name: 'Krush',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 10, max: 14 },
    betaAcidPercent: { min: 6.3, max: 9.4 },
    cohumulonePercentOfAlpha: { min: 35, max: 40 },
    totalOilMlPer100g: { min: 1.5, max: 3 },
    oilComposition: {
      myrcenePercent: 45,
      humulenePercent: 15,
      caryophyllenePercent: 13,
      farnesenePercent: 0.1,
    },
    substitutes: ['citra', 'mosaic', 'elani', 'galaxy'],
    flavorTags: ['orange', 'mango', 'guava', 'peach', 'berry', 'resinous'],
    description:
      'HBC 586, commercialised in 2024 as Krush after seventeen years as a brewer favorite in the experimental pipeline. Orange, mango, guava and peach with a resinous undercurrent — a fruit-cocktail hop that translates cleanly from the rub to the glass. High cohumulone; keep early additions light.',
    tags: ['usa', 'yakima', 'dual-purpose', 'hbc-586', 'proprietary'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/krush',
    provenance: 'spec-sheet',
  },
  {
    id: 'lemondrop',
    name: 'Lemondrop',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 5, max: 7 },
    betaAcidPercent: { min: 4, max: 6 },
    cohumulonePercentOfAlpha: { min: 28, max: 32 },
    totalOilMlPer100g: { min: 1.2, max: 2 },
    oilComposition: null,
    substitutes: ['cascade', 'cashmere', 'sorachi-ace'],
    flavorTags: ['lemon', 'mint', 'green-tea', 'citrus', 'melon'],
    description:
      'A Cascade seedling that leans hard into lemon zest and mint with a green-tea dryness. Low alpha and clean, so it suits late additions in wheat beers, blondes and summer session ales.',
    tags: ['usa', 'aroma', 'hops-lc'],
    url: 'https://hopsteiner.us/variety-data-sheets/lemondrop/',
    provenance: 'spec-sheet',
  },
  {
    id: 'liberty',
    name: 'Liberty',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 3, max: 6 },
    betaAcidPercent: { min: 3, max: 4.5 },
    cohumulonePercentOfAlpha: { min: 24, max: 30 },
    totalOilMlPer100g: { min: 0.6, max: 1.2 },
    oilComposition: {
      myrcenePercent: 30,
      humulenePercent: 37,
      caryophyllenePercent: 10,
      farnesenePercent: 0.5,
    },
    storageStability: {
      alphaLostPercentSixMonths: 55,
      source: 'https://morebeer.com/blogs/articles/hop-storage',
    },
    substitutes: ['hallertauer-mittelfrueh', 'mount-hood', 'crystal'],
    flavorTags: ['floral', 'spicy', 'herbal', 'noble', 'lemon'],
    description:
      'The closest US-grown thing to Hallertauer Mittelfrueh, with the same humulene-dominant floral spice at slightly lower intensity. Made for German lagers, helles and pilsner where a clean noble aroma is the whole point.',
    tags: ['usa', 'oregon', 'aroma', 'noble-type', 'hallertau-derived'],
    url: 'https://hopsteiner.us/variety-data-sheets/liberty/',
    provenance: 'spec-sheet',
  },
  {
    id: 'lorien',
    name: 'Lórien',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 4, max: 5.5 },
    betaAcidPercent: { min: 7, max: 9 },
    cohumulonePercentOfAlpha: { min: 20, max: 25 },
    totalOilMlPer100g: { min: 1.8, max: 2.2 },
    oilComposition: null,
    substitutes: ['hallertauer-mittelfrueh', 'saphir', 'meridian', 'cashmere'],
    flavorTags: ['citrus', 'melon', 'floral', 'hay', 'spicy'],
    description:
      'Indie Hops’ low-gravity hop: tangy citrus and fresh melon over wildflower, sweet hay and a cinnamon-spice finish, with more oil than the alpha suggests. Built for lagers, kölsch and session ales where the malt still has to speak; brewers also sneak it into pale IPA for lift without bite.',
    tags: ['usa', 'oregon', 'aroma', 'indie-hops', 'osu', 'proprietary'],
    url: 'https://indiehops.com/hops/lorien',
    provenance: 'spec-sheet',
  },
  {
    id: 'loral',
    name: 'Loral',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 10, max: 12.5 },
    betaAcidPercent: { min: 4, max: 6 },
    cohumulonePercentOfAlpha: { min: 21, max: 25 },
    totalOilMlPer100g: { min: 1.5, max: 2.5 },
    oilComposition: {
      myrcenePercent: 38,
      humulenePercent: 25,
      caryophyllenePercent: 12,
      farnesenePercent: 1,
    },
    substitutes: ['mount-hood', 'cascade', 'hallertauer-mittelfrueh', 'comet'],
    flavorTags: ['floral', 'dark-fruit', 'pepper', 'citrus', 'noble'],
    description:
      'A Hop Breeding Company variety pitched as a modern noble: floral and peppery with dark fruit and a lemon edge, but at IPA-grade alpha. Bridges classic lager aroma and American hopping, so it shines in pilsners, saisons and hoppy amber ales.',
    tags: ['usa', 'yakima', 'dual-purpose', 'hbc-291', 'noble-type', 'proprietary'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/loralr',
    provenance: 'spec-sheet',
  },
  {
    id: 'lotus',
    name: 'Lotus',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 13, max: 17 },
    betaAcidPercent: { min: 5.5, max: 6.5 },
    cohumulonePercentOfAlpha: { min: 33, max: 39 },
    totalOilMlPer100g: { min: 1.5, max: 2.5 },
    oilComposition: null,
    substitutes: ['el-dorado', 'sabro', 'amarillo', 'cashmere'],
    flavorTags: ['orange', 'vanilla', 'grape', 'tropical', 'berry'],
    description:
      'A Hopsteiner release (ex-X06297) with Eastern Gold, Apollo and Cascade in the family and a wild American male on top. Orange, vanilla and candied grape over tropical fruit — creamy rather than resinous. High alpha lets it bitter, but the profile is wasted anywhere but late.',
    tags: ['usa', 'hopsteiner', 'dual-purpose', 'neomexicanus', 'proprietary'],
    url: 'https://hopsteiner.us/variety-data-sheets/lotus/',
    provenance: 'spec-sheet',
  },
  {
    id: 'luminosa',
    name: 'Luminosa',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 9, max: 12 },
    betaAcidPercent: { min: 4, max: 5 },
    cohumulonePercentOfAlpha: { min: 26, max: 31 },
    totalOilMlPer100g: { min: 1.8, max: 2.2 },
    oilComposition: {
      myrcenePercent: 48,
      humulenePercent: 12,
      caryophyllenePercent: 5,
      farnesenePercent: 0,
    },
    substitutes: ['strata', 'citra', 'el-dorado', 'sorachi-ace'],
    flavorTags: ['peach', 'mango', 'orange', 'papaya', 'guava', 'tropical'],
    description:
      'An Indie Hops Sorachi Ace seedling that kept the citrus and added peach-mango lemonade, candied orange peel, papaya and guava — and left the dill behind. Almost no pine or resin, which makes it unusually easy in hazies, pale ales and hoppy lagers.',
    tags: ['usa', 'oregon', 'aroma', 'indie-hops', 'osu', 'proprietary'],
    url: 'https://indiehops.com/hops/luminosa',
    provenance: 'spec-sheet',
  },
  {
    id: 'lupocore',
    name: 'LUPOCORE',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 8.1, max: 17.3 },
    betaAcidPercent: { min: 0, max: 0 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 0, max: 0 },
    oilComposition: null,
    substitutes: ['citra', 'mosaic', 'columbus'],
    flavorTags: ['concentrated', 'resinous'],
    description:
      'Cold-processed concentrate, sold per variety like LUPOMAX and pitched at brewhouse yield as much as at flavor. Published alpha ran 8.1 % for Cascade to 17.3 % for Columbus, so the band here spans varieties rather than lots: read your own bag.',
    tags: ['usa', 'lupulin', 'concentrated', 'product'],
    kind: 'product',
    defaultForm: 'cryo',
    provenance: 'representative',
  },
  {
    id: 'lupomax',
    name: 'LUPOMAX',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 10.5, max: 20 },
    betaAcidPercent: { min: 0, max: 0 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 0, max: 0 },
    oilComposition: null,
    substitutes: ['citra', 'mosaic', 'el-dorado'],
    flavorTags: ['concentrated', 'intense'],
    description:
      'Concentrated lupulin pellets sold per variety, with less vegetative matter than a T90 and roughly double the alpha. Published alpha ran 10.5 % for Talus to 20 % for BRU-1 in the 2025 crop, so enter the figure on your bag rather than the band shown here. Substitute at 50-70 % of the pellet weight a recipe calls for.',
    tags: ['usa', 'lupulin', 'concentrated', 'product'],
    kind: 'product',
    defaultForm: 'cryo',
    provenance: 'representative',
  },
  {
    id: 'mackinac',
    name: 'Mackinac',
    origin: 'United States',
    growingRegion: 'Michigan',
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 9, max: 13 },
    betaAcidPercent: { min: 2.5, max: 4 },
    cohumulonePercentOfAlpha: { min: 26, max: 32 },
    totalOilMlPer100g: { min: 1.2, max: 1.8 },
    oilComposition: {
      myrcenePercent: 32,
      humulenePercent: 21,
      caryophyllenePercent: 10,
      farnesenePercent: 0.2,
    },
    substitutes: ['michigan-copper', 'chinook', 'mosaic', 'citra'],
    flavorTags: ['tropical', 'citrus', 'melon', 'papaya', 'spice'],
    description:
      'The sister plant to Michigan Copper, a Chinook cross from Great Lakes Hops with fruit-punch citrus, melon, papaya and a peppery floral edge. Higher alpha than Copper, so it can bitter, but the super-aroma character is wasted anywhere but late.',
    tags: ['usa', 'michigan', 'great-lakes', 'glh', 'proprietary', 'chinook-cross'],
    url: 'https://www.greatlakeshops.com/mackinactrade.html',
    provenance: 'spec-sheet',
  },
  {
    id: 'mckenzie',
    name: 'McKenzie',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 8, max: 10 },
    betaAcidPercent: { min: 4, max: 5 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.2, max: 2 },
    oilComposition: null,
    substitutes: ['mosaic', 'el-dorado', 'amarillo', 'luminosa'],
    flavorTags: ['berry', 'mango', 'papaya', 'orange', 'stone-fruit'],
    description:
      'An Indie Hops / Oregon State selection named for the McKenzie River: raspberry and tropical fruit over orange and nectarine, with a soft minty-tea edge. Built for juicy pale ales; less widely planted than Strata, so treat it as a seasonal find rather than a year-round staple.',
    tags: ['usa', 'oregon', 'aroma', 'indie-hops', 'osu', 'proprietary'],
    provenance: 'representative',
  },
  {
    id: 'meridian',
    name: 'Meridian',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 5, max: 6.5 },
    betaAcidPercent: { min: 7, max: 8 },
    cohumulonePercentOfAlpha: { min: 48, max: 52 },
    totalOilMlPer100g: { min: 1.1, max: 1.6 },
    oilComposition: {
      myrcenePercent: 52,
      humulenePercent: 13,
      caryophyllenePercent: 6,
      farnesenePercent: 0.5,
    },
    substitutes: ['lemondrop', 'cashmere', 'lorien', 'cascade'],
    flavorTags: ['lemon', 'boysenberry', 'mint', 'pineapple', 'floral'],
    description:
      'Indie Hops’ first proprietary, a rogue seedling they thought was Columbia. Meyer lemon, boysenberry, spearmint and pineapple — a top-note hop that brightens a bill rather than filling it. High cohumulone and modest oil, so dose it generously in the dry hop and do not bitter with it.',
    tags: ['usa', 'oregon', 'aroma', 'indie-hops', 'proprietary'],
    url: 'https://indiehops.com/hops/meridian',
    provenance: 'spec-sheet',
  },
  {
    id: 'michigan-copper',
    name: 'Michigan Copper',
    origin: 'United States',
    growingRegion: 'Michigan',
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 8.5, max: 12 },
    betaAcidPercent: { min: 2.4, max: 4 },
    cohumulonePercentOfAlpha: { min: 27, max: 38 },
    totalOilMlPer100g: { min: 1, max: 1.9 },
    oilComposition: null,
    substitutes: ['mackinac', 'chinook', 'mosaic', 'el-dorado'],
    flavorTags: ['fruit-punch', 'cherry', 'pineapple', 'floral', 'tropical'],
    description:
      'A Great Lakes Hops Chinook cross released as a super-aroma hop: fruit punch, black cherry, pineapple and a hibiscus-candy note. Late additions and dry hops are the point; the sister plant Mackinac is the louder tropical sibling. Oil fractions move a lot with crop year, so we do not invent a breakdown.',
    tags: ['usa', 'michigan', 'great-lakes', 'glh', 'proprietary', 'chinook-cross'],
    url: 'https://www.greatlakeshops.com/michigan-coppertrade.html',
    provenance: 'spec-sheet',
  },
  {
    id: 'millennium',
    name: 'Millennium',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'bittering',
    alphaAcidPercent: { min: 15.5, max: 18.5 },
    betaAcidPercent: { min: 4.3, max: 6.5 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.6, max: 3 },
    oilComposition: {
      myrcenePercent: 45,
      humulenePercent: 20,
      caryophyllenePercent: 9,
      farnesenePercent: 0.5,
    },
    substitutes: ['nugget', 'columbus', 'magnum', 'galena'],
    flavorTags: ['resinous', 'herbal', 'spicy', 'earthy'],
    description:
      'A daughter of Nugget from 2000, and a hop bought by the bale rather than chosen: very high alpha, a mild resinous-herbal character and nothing that argues with the beer downstream. This is what a commercial brewery reaches for when the kettle addition only has to supply IBU.',
    tags: ['usa', 'high-alpha', 'bittering', 'clean'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/millennium',
    provenance: 'representative',
  },
  {
    id: 'mjf-blend',
    name: 'MJF Blend',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 12.4, max: 12.4 },
    betaAcidPercent: { min: 5.7, max: 5.7 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 2, max: 2 },
    oilComposition: {
      myrcenePercent: 47.8,
      humulenePercent: 14.8,
      caryophyllenePercent: 8.9,
      farnesenePercent: 1.1,
    },
    substitutes: ['citra', 'el-dorado', 'elani'],
    flavorTags: ['citrus', 'melon', 'tropical', 'sweet-fruit'],
    description:
      "A vendor blend, not a variety: equal parts Citra, Citra Lupomax, Elani and El Dorado, some of it off Loza Farms. The proportions are the seller's and can change between crops, so treat the analysis as this year's lot rather than a specification.",
    tags: ['usa', 'blend', 'proprietary'],
    kind: 'blend',
    provenance: 'representative',
  },
  {
    id: 'mosaic',
    name: 'Mosaic',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 11.5, max: 13.5 },
    betaAcidPercent: { min: 3.2, max: 3.9 },
    cohumulonePercentOfAlpha: { min: 24, max: 26 },
    totalOilMlPer100g: { min: 1, max: 1.7 },
    oilComposition: {
      myrcenePercent: 52,
      humulenePercent: 16,
      caryophyllenePercent: 8,
      farnesenePercent: 0.5,
    },
    substitutes: ['citra', 'simcoe', 'galaxy'],
    flavorTags: ['blueberry', 'mango', 'tropical', 'dank', 'stone-fruit'],
    description:
      'A Simcoe daughter that layers blueberry and mango over a dank, resinous base — complex enough to carry a beer alone. Standard issue in hazy and West Coast IPA alike, usually paired with Citra for a full tropical spread.',
    tags: ['usa', 'yakima', 'dual-purpose', 'hbc-369', 'proprietary'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/mosaicr',
    provenance: 'spec-sheet',
  },
  {
    id: 'mount-hood',
    name: 'Mount Hood',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 4, max: 7 },
    betaAcidPercent: { min: 5, max: 8 },
    cohumulonePercentOfAlpha: { min: 21, max: 23 },
    totalOilMlPer100g: { min: 1, max: 1.7 },
    oilComposition: {
      myrcenePercent: 35,
      humulenePercent: 30,
      caryophyllenePercent: 14,
      farnesenePercent: 0.5,
    },
    /** Table I's "Mt. Hood". */
    storageStability: {
      alphaLostPercentSixMonths: 45,
      source: 'https://morebeer.com/blogs/articles/hop-storage',
    },
    substitutes: ['hallertauer-mittelfrueh', 'liberty', 'crystal'],
    flavorTags: ['herbal', 'spicy', 'floral', 'noble', 'woody'],
    description:
      'Another Hallertau-derived Oregon aroma hop, a shade more herbal and pungent than Liberty. Reliable and widely stocked, it is the safe domestic choice for lagers, altbier and any beer wanting restrained spicy aroma.',
    tags: ['usa', 'oregon', 'aroma', 'noble-type', 'hallertau-derived'],
    url: 'https://www.crosbyhops.com/hops/varieties/mthood',
    provenance: 'spec-sheet',
  },
  {
    id: 'mystique',
    name: 'Mystique',
    origin: 'United States',
    growingRegion: 'Michigan',
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 3.5, max: 5.5 },
    betaAcidPercent: { min: 3, max: 5 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 0.6, max: 1.2 },
    oilComposition: null,
    substitutes: ['saaz', 'willamette', 'fuggle', 'east-kent-goldings'],
    flavorTags: ['noble', 'herbal', 'citrus', 'floral', 'earthy'],
    description:
      'A found Michigan hop from an old Stroh’s-era yard along the Telegraph corridor, with Saaz genetics and a lemon-lime herbal noble profile. Not the Žatec landrace — a local selection that stands in for imported Saaz in lagers, kölsch and saison. Oil breakdowns are unpublished; trust the bag for bittering.',
    tags: ['usa', 'michigan', 'great-lakes', 'saaz-type', 'strohs-saaz', 'landrace'],
    provenance: 'representative',
  },
  {
    id: 'nugget',
    name: 'Nugget',
    origin: 'United States',
    growingRegion: 'Oregon',
    cultivar: 'nugget',
    purpose: 'bittering',
    alphaAcidPercent: { min: 12, max: 15 },
    betaAcidPercent: { min: 4, max: 6 },
    cohumulonePercentOfAlpha: { min: 22, max: 30 },
    totalOilMlPer100g: { min: 1.5, max: 2.5 },
    oilComposition: {
      myrcenePercent: 54,
      humulenePercent: 18,
      caryophyllenePercent: 8,
      farnesenePercent: 0.5,
    },
    storageStability: {
      alphaLostPercentSixMonths: 25,
      source: 'https://morebeer.com/blogs/articles/hop-storage',
    },
    substitutes: ['galena', 'columbus', 'chinook'],
    flavorTags: ['herbal', 'resinous', 'pear', 'spicy'],
    description:
      'High alpha with unusually low cohumulone for its class, so the bitterness comes across firm but smooth. A dependable kettle hop for IPAs and stouts, with a green, herbal-resinous aroma if you add it late.',
    tags: ['usa', 'oregon', 'bittering', 'high-alpha', 'classic'],
    url: 'https://www.crosbyhops.com/hops/varieties/nugget',
    provenance: 'spec-sheet',
  },
  {
    id: 'nugget-michigan',
    name: 'Nugget (Michigan)',
    origin: 'United States',
    growingRegion: 'Michigan',
    cultivar: 'nugget',
    purpose: 'bittering',
    alphaAcidPercent: { min: 13, max: 15.5 },
    betaAcidPercent: { min: 7, max: 10.5 },
    cohumulonePercentOfAlpha: { min: 30, max: 38 },
    totalOilMlPer100g: { min: 0.8, max: 1.5 },
    oilComposition: {
      myrcenePercent: 47,
      humulenePercent: 15,
      caryophyllenePercent: 8,
      farnesenePercent: 0.5,
    },
    substitutes: ['nugget', 'galena', 'columbus', 'chinook'],
    flavorTags: ['herbal', 'resinous', 'spicy', 'green'],
    description:
      'Michigan-grown Nugget. Alpha stays in the high-alpha bittering band, but Michigan lots run a distinctly higher beta and cohumulone than the Oregon crop, with less total oil. Still a kettle hop; the green herbal note is quieter than Oregon Nugget.',
    tags: ['usa', 'michigan', 'great-lakes', 'bittering', 'high-alpha'],
    provenance: 'spec-sheet',
  },
  {
    id: 'palisade',
    name: 'Palisade',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 5.5, max: 9.5 },
    betaAcidPercent: { min: 6, max: 8 },
    cohumulonePercentOfAlpha: { min: 24, max: 30 },
    totalOilMlPer100g: { min: 1, max: 1.7 },
    oilComposition: {
      myrcenePercent: 35,
      humulenePercent: 20,
      caryophyllenePercent: 10,
      farnesenePercent: 0.5,
    },
    substitutes: ['willamette', 'glacier', 'cascade'],
    flavorTags: ['apricot', 'grass', 'floral', 'stone-fruit', 'clean'],
    description:
      'A Yakima Chief selection out of Tettnanger stock giving clean apricot and cut-grass aroma without much bite. Good late-addition hop for pale ales, ESBs and anywhere you want fruit that stays in the background.',
    tags: ['usa', 'yakima', 'aroma', 'yakima-chief', 'ycr-4'],
    url: 'https://yakimachiefranches.com/create/brands/palisade',
    provenance: 'spec-sheet',
  },
  {
    id: 'paradigm',
    name: 'Paradigm',
    origin: 'United States',
    growingRegion: 'Michigan',
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 8, max: 11 },
    betaAcidPercent: { min: 4, max: 6 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.5, max: 2.3 },
    oilComposition: null,
    substitutes: ['mosaic', 'citra', 'galaxy', 'saaz'],
    flavorTags: ['peach', 'pear', 'tropical', 'candy', 'melon'],
    description:
      'A Great Lakes Hops super-aroma hop: peach, pear, tropical candy, mint and melon. Keep it out of the early boil — late and dry hop is where the fruit shows, and a long kettle rest turns it herbal. Oil fractions are unpublished.',
    tags: ['usa', 'michigan', 'great-lakes', 'glh', 'proprietary'],
    url: 'https://www.greatlakeshops.com/paradigmtrade.html',
    provenance: 'representative',
  },
  {
    id: 'pekko',
    name: 'Pekko',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 12, max: 12 },
    betaAcidPercent: { min: 3.5, max: 4.5 },
    cohumulonePercentOfAlpha: { min: 27, max: 30 },
    totalOilMlPer100g: { min: 2.1, max: 2.7 },
    oilComposition: {
      myrcenePercent: 50.5,
      humulenePercent: 13.5,
      caryophyllenePercent: 12,
      farnesenePercent: 0.5,
    },
    substitutes: ['sterling', 'crystal', 'cascade', 'santiam'],
    flavorTags: ['floral', 'citrus', 'herbal', 'mint', 'pear'],
    description:
      'An ADHA release named for the Finnish god of fields, and a hard one to place: floral and citrus over herbal, earthy notes with mint and pear at the edges. It suits a saison or a gose better than it suits an IPA. Three sellers publish its alpha and none publishes a beta acid, so the beta band here is a reported figure rather than one off a sheet.',
    tags: ['usa', 'adha', 'aroma', 'dual'],
    provenance: 'representative',
  },
  {
    id: 'pha-hop-extract',
    name: 'PHA Hop Extract',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 0, max: 0 },
    betaAcidPercent: { min: 0, max: 0 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 0, max: 0 },
    oilComposition: null,
    substitutes: [],
    flavorTags: ['floral', 'fruity'],
    description:
      "A hop-derived aroma extract that is, in its maker's words, completely free of alpha acids — building blocks for flavor design rather than a bittering addition. No alpha, so no IBU.",
    tags: ['usa', 'extract', 'aroma', 'product'],
    kind: 'product',
    defaultForm: 'aroma-extract',
    provenance: 'representative',
  },
  {
    id: 'pink',
    name: 'Pink',
    origin: 'United States',
    growingRegion: 'Idaho',
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 11.4, max: 11.4 },
    betaAcidPercent: { min: 6.23, max: 6.23 },
    cohumulonePercentOfAlpha: { min: 24.4, max: 24.4 },
    totalOilMlPer100g: { min: 1.53, max: 1.53 },
    oilComposition: null,
    substitutes: ['idaho-7', 'idaho-gem', 'citra'],
    flavorTags: ['citrus', 'stone-fruit', 'strawberry', 'honey'],
    description:
      'Trial number ID-158 from Jackson Farms in Wilder, the growers behind Idaho 7. Strawberry is the note people fix on, over citrus and stone fruit with honey and apple underneath. Cohumulone under 25 % keeps the bitterness soft.',
    tags: ['usa', 'idaho', 'jackson-farms'],
    provenance: 'representative',
  },
  {
    id: 'pink-boots-blend',
    name: 'Pink Boots Blend',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 12.8, max: 12.8 },
    betaAcidPercent: { min: 0, max: 0 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 0, max: 0 },
    oilComposition: null,
    substitutes: ['centennial', 'ekuanot', 'mosaic'],
    flavorTags: ['citrus', 'tropical', 'stone-fruit'],
    description:
      'Blended each year by Pink Boots Society members from their own sensory selection, with a dollar a pound going to the society. The 2025 crop is Centennial, Ekuanot, HBC 638 and Mosaic at 12.8 % alpha — and it is a different blend every year, so treat that figure as this crop rather than the product. Yakima Chief publish no beta or oil for it, which is why both read zero here.',
    tags: ['usa', 'blend', 'proprietary', 'charity'],
    kind: 'blend',
    provenance: 'representative',
  },
  {
    id: 'prysma',
    name: 'PRYSMA',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 0, max: 0 },
    betaAcidPercent: { min: 0, max: 0 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 0, max: 0 },
    oilComposition: null,
    substitutes: [],
    flavorTags: ['fruity', 'clean', 'intense'],
    description:
      'A flowable flavor platform, 100 % hops in a hop-derived carrier, dosable at any point in the process. Built for variety-true flavor without bitterness, which is why it adds no IBU.',
    tags: ['usa', 'extract', 'aroma', 'product'],
    kind: 'product',
    defaultForm: 'aroma-extract',
    provenance: 'representative',
  },
  {
    id: 'sabro',
    name: 'Sabro',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 12, max: 16 },
    betaAcidPercent: { min: 4.5, max: 7.5 },
    cohumulonePercentOfAlpha: { min: 20, max: 25 },
    totalOilMlPer100g: { min: 2.5, max: 4 },
    oilComposition: {
      myrcenePercent: 45,
      humulenePercent: 12,
      caryophyllenePercent: 8,
      farnesenePercent: 8,
    },
    substitutes: ['citra', 'el-dorado', 'sorachi-ace', 'talus'],
    flavorTags: ['coconut', 'tangerine', 'tropical', 'cedar', 'stone-fruit'],
    description:
      'A neomexicanus-derived hop with an unmistakable coconut and tangerine signature over cedar and stone fruit. Polarising and potent — use a fraction of the dry hop charge you would give Citra, or it takes over the beer.',
    tags: ['usa', 'yakima', 'dual-purpose', 'hbc-438', 'neomexicanus', 'proprietary'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/sabror',
    provenance: 'spec-sheet',
  },
  {
    id: 'samsquanch',
    name: 'SAMSQUANCH',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 8.4, max: 8.4 },
    betaAcidPercent: { min: 6.3, max: 6.3 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.5, max: 1.5 },
    oilComposition: null,
    substitutes: ['amarillo', 'sorachi-ace', 'cascade'],
    flavorTags: ['orange', 'lemon', 'lime', 'dill', 'herbal'],
    description:
      "A seller's blend of Amarillo, Sorachi Ace and a variety they will not name, and it tastes like that sounds: citrus with a dill-and-herb edge that nothing else here has. The recipe is theirs and can change between crops, so the analysis is one lot rather than a specification.",
    tags: ['usa', 'blend', 'proprietary'],
    kind: 'blend',
    provenance: 'representative',
  },
  {
    id: 'santiam',
    name: 'Santiam',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 5, max: 7 },
    betaAcidPercent: { min: 6, max: 8.5 },
    cohumulonePercentOfAlpha: { min: 20, max: 24 },
    totalOilMlPer100g: { min: 1.3, max: 1.8 },
    oilComposition: {
      myrcenePercent: 32,
      humulenePercent: 22,
      caryophyllenePercent: 8,
      farnesenePercent: 14,
    },
    substitutes: ['hallertauer-mittelfrueh', 'tettnanger', 'mount-hood', 'liberty'],
    flavorTags: ['floral', 'herbal', 'spicy', 'noble'],
    description:
      'A Tettnanger-descended Oregon aroma hop, notable for a genuinely high farnesene fraction that gives it a green, floral lift. The pick when a domestic hop needs to pass for continental in a pilsner or a German wheat.',
    tags: ['usa', 'oregon', 'aroma', 'noble-type', 'tettnang-derived'],
    url: 'https://hopsteiner.us/variety-data-sheets/santiam/',
    provenance: 'spec-sheet',
  },
  {
    id: 'sasquatch',
    name: 'Sasquatch',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 7, max: 8 },
    betaAcidPercent: { min: 8, max: 9 },
    cohumulonePercentOfAlpha: { min: 30, max: 40 },
    totalOilMlPer100g: { min: 0.5, max: 1 },
    oilComposition: {
      myrcenePercent: 60,
      humulenePercent: 14,
      caryophyllenePercent: 6,
      farnesenePercent: 8,
    },
    substitutes: ['cascade', 'crystal', 'willamette', 'sterling'],
    flavorTags: ['apple-blossom', 'orange', 'hay', 'tangerine'],
    description:
      'Found growing wild in British Columbia and now grown commercially in the Pacific Northwest. Beta acid sits above alpha, which is a wild-hop signature, and the aroma is orchard rather than tropical: apple blossom, orange zest and cut hay.',
    tags: ['usa', 'wild', 'aroma'],
    provenance: 'representative',
  },
  {
    id: 'simcoe',
    name: 'Simcoe',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 12, max: 14 },
    betaAcidPercent: { min: 4, max: 5 },
    cohumulonePercentOfAlpha: { min: 15, max: 20 },
    totalOilMlPer100g: { min: 2, max: 2.5 },
    oilComposition: {
      myrcenePercent: 62,
      humulenePercent: 13,
      caryophyllenePercent: 7,
      farnesenePercent: 0.5,
    },
    substitutes: ['mosaic', 'citra', 'chinook'],
    flavorTags: ['pine', 'passionfruit', 'dank', 'apricot', 'resinous'],
    description:
      'Pine and passionfruit over a catty, dank base, with very low cohumulone that makes it clean enough to bitter with. One of the few hops that genuinely works at every addition point in a West Coast IPA.',
    tags: ['usa', 'yakima', 'dual-purpose', 'ycr-14', 'west-coast', 'proprietary'],
    url: 'https://www.crosbyhops.com/hops/varieties/simcoe',
    provenance: 'spec-sheet',
  },
  {
    id: 'sonnet',
    name: 'Sonnet',
    origin: 'United States',
    growingRegion: 'Oregon',
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 4.5, max: 4.7 },
    betaAcidPercent: { min: 3.2, max: 3.7 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 0.5, max: 1 },
    oilComposition: null,
    substitutes: ['east-kent-goldings', 'us-goldings', 'crystal', 'willamette'],
    flavorTags: ['honeysuckle', 'lemon', 'floral', 'citrus'],
    description:
      "Oregon's answer to Kent Golding, and close enough to sit in the same beers: floral honeysuckle where the English hop gives earth, with a lemon lift that gives it away. Low alpha and low oil, so it is a late hop.",
    tags: ['usa', 'oregon', 'aroma', 'goldings-type'],
    provenance: 'representative',
  },
  {
    id: 'sorachi-ace',
    name: 'Sorachi Ace',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 10, max: 16 },
    betaAcidPercent: { min: 6, max: 7 },
    cohumulonePercentOfAlpha: { min: 23, max: 26 },
    totalOilMlPer100g: { min: 2, max: 3 },
    oilComposition: {
      myrcenePercent: 50,
      humulenePercent: 20,
      caryophyllenePercent: 8,
      farnesenePercent: 0.5,
    },
    substitutes: ['lemondrop', 'sabro', 'cascade'],
    flavorTags: ['lemon', 'dill', 'coconut', 'herbal', 'lemongrass'],
    description:
      'Bred by Sapporo in Japan but grown commercially in the Pacific Northwest, this is the lemon-and-dill hop — sharply citric with a coconut-tinged herbal edge. Brilliant in saisons and wheat beers, divisive everywhere else.',
    tags: ['usa', 'japanese-bred', 'dual-purpose', 'sapporo'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/sorachi-ace',
    provenance: 'spec-sheet',
  },
  {
    id: 'spectrum',
    name: 'SPECTRUM',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 0, max: 0 },
    betaAcidPercent: { min: 0, max: 0 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 0, max: 0 },
    oilComposition: null,
    substitutes: [],
    flavorTags: ['citrus', 'tropical', 'clean'],
    description:
      'A fully dispersible liquid hop product for dry hopping, made from 100 % hop material and mixing into cold beer without a bag or a whirlpool. It is a dry-hop substitute, so it contributes aroma and no bitterness.',
    tags: ['usa', 'extract', 'aroma', 'product', 'dry-hop'],
    kind: 'product',
    defaultForm: 'aroma-extract',
    provenance: 'representative',
  },
  {
    id: 'sticky-fingers',
    name: 'Sticky Fingers',
    origin: 'United States',
    growingRegion: 'Michigan',
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 8, max: 8 },
    betaAcidPercent: { min: 4.41, max: 4.41 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 0.73, max: 0.73 },
    oilComposition: null,
    substitutes: ['cascade', 'centennial', 'mackinac'],
    flavorTags: ['dank', 'fruity', 'spicy'],
    description:
      'Seventeen Michigan varieties blended off one harvest — Cascade, Centennial, Chinook, Comet, El Dorado, Mackinac, Vista and more — and sold as a snapshot of that season rather than a repeatable product. Dank and complex, and different every year by design.',
    tags: ['usa', 'michigan', 'blend', 'proprietary'],
    kind: 'blend',
    provenance: 'representative',
  },
  {
    id: 'strata',
    name: 'Strata',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 11, max: 15 },
    betaAcidPercent: { min: 4.5, max: 5.5 },
    cohumulonePercentOfAlpha: { min: 22, max: 26 },
    totalOilMlPer100g: { min: 2.5, max: 3.5 },
    oilComposition: {
      myrcenePercent: 55,
      humulenePercent: 12,
      caryophyllenePercent: 7,
      farnesenePercent: 0.5,
    },
    substitutes: ['citra', 'mosaic', 'simcoe', 'luminosa', 'audacia'],
    flavorTags: ['passionfruit', 'strawberry', 'dank', 'grapefruit', 'tropical'],
    description:
      'An Oregon State release via Indie Hops that manages passionfruit and strawberry alongside a heavy dank, cannabis-like base. Very high oil makes it a standout dry hop, and it holds its own as a single-hop IPA.',
    tags: ['usa', 'oregon', 'dual-purpose', 'indie-hops', 'proprietary'],
    url: 'https://indiehops.com/hops/strata',
    provenance: 'spec-sheet',
  },
  {
    id: 'saaz-michigan',
    name: 'Saaz (Michigan)',
    origin: 'United States',
    growingRegion: 'Michigan',
    cultivar: 'saaz',
    purpose: 'aroma',
    alphaAcidPercent: { min: 3, max: 5.5 },
    betaAcidPercent: { min: 3, max: 5 },
    cohumulonePercentOfAlpha: { min: 20, max: 26 },
    totalOilMlPer100g: { min: 0.4, max: 1.1 },
    oilComposition: null,
    substitutes: ['saaz', 'sterling', 'lublin', 'hersbrucker'],
    flavorTags: ['noble', 'herbal', 'spicy', 'floral', 'earthy'],
    description:
      'Michigan-grown Saaz from the Great Lakes farms, sometimes sold as Saaz Super. Cooler nights keep it closer to the Czech landrace than Yakima lots do — still herbal and spicy, usually a little more alpha. Published oil breakdowns are thin, so the chemistry here is class-typical; trust the bag analysis for bittering.',
    tags: ['usa', 'michigan', 'great-lakes', 'saaz', 'saaz-super', 'noble', 'landrace'],
    provenance: 'representative',
  },
  {
    id: 'saaz-washington',
    name: 'Saaz (Washington)',
    origin: 'United States',
    growingRegion: 'Yakima Valley',
    cultivar: 'saaz',
    purpose: 'aroma',
    alphaAcidPercent: { min: 3, max: 6 },
    betaAcidPercent: { min: 3, max: 4.5 },
    cohumulonePercentOfAlpha: { min: 24, max: 28 },
    totalOilMlPer100g: { min: 0.5, max: 1 },
    oilComposition: {
      myrcenePercent: 35,
      humulenePercent: 20,
      caryophyllenePercent: 7,
      farnesenePercent: 10,
    },
    substitutes: ['saaz', 'sterling', 'santiam', 'lublin'],
    flavorTags: ['noble', 'herbal', 'spicy', 'citrus', 'floral'],
    description:
      'Yakima-grown Saaz. Same plant as the Žatec landrace, but Washington terroir pushes the alpha up and the farnesene down, so the beer reads more herbal-citrus than spicy-earth. A fine noble charge in an American pilsner; not a silent swap for Czech Saaz in a Bohemian lager that depends on that farnesene spike.',
    tags: ['usa', 'yakima', 'washington', 'saaz', 'noble', 'landrace'],
    provenance: 'spec-sheet',
  },
  {
    id: 'sterling',
    name: 'Sterling',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 6, max: 9 },
    betaAcidPercent: { min: 4, max: 6 },
    cohumulonePercentOfAlpha: { min: 22, max: 28 },
    totalOilMlPer100g: { min: 1.3, max: 1.9 },
    oilComposition: {
      myrcenePercent: 46,
      humulenePercent: 21,
      caryophyllenePercent: 6,
      farnesenePercent: 0,
    },
    substitutes: ['saaz', 'lublin', 'santiam', 'mount-hood'],
    flavorTags: ['spicy', 'floral', 'citrus', 'herbal', 'noble'],
    description:
      'A USDA Saaz-type bred for Oregon: spicy and floral with a citrus lift, more assertive than Czech Saaz but in the same family. The default domestic noble stand-in for pilsner, kölsch and Belgian ales when European aroma hops are short.',
    tags: ['usa', 'oregon', 'aroma', 'noble-type', 'usda', 'saaz-type'],
    url: 'https://www.crosbyhops.com/hops/varieties/sterling',
    provenance: 'spec-sheet',
  },
  {
    id: 'sultana',
    name: 'Sultana',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 13, max: 15.5 },
    betaAcidPercent: { min: 4.5, max: 5.5 },
    cohumulonePercentOfAlpha: { min: 22, max: 26 },
    totalOilMlPer100g: { min: 2.5, max: 3.5 },
    oilComposition: null,
    substitutes: ['columbus', 'chinook', 'citra', 'el-dorado'],
    flavorTags: ['pineapple', 'citrus', 'pine', 'stone-fruit', 'tropical'],
    description:
      'Sold as Denali until 2021 and renamed Sultana; the hop itself is unchanged. Pineapple and citrus over a firm pine backbone, with enough alpha to bitter — a good way to give a West Coast IPA tropical fruit without losing its resinous spine.',
    tags: ['usa', 'yakima', 'dual-purpose', 'denali', 'hopsteiner', 'proprietary'],
    url: 'https://hopsteiner.us/variety-data-sheets/sultana/',
    provenance: 'spec-sheet',
  },
  {
    id: 'summit',
    name: 'Summit',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'bittering',
    alphaAcidPercent: { min: 16, max: 19 },
    betaAcidPercent: { min: 4, max: 6 },
    cohumulonePercentOfAlpha: { min: 26, max: 33 },
    totalOilMlPer100g: { min: 1.5, max: 2.5 },
    oilComposition: {
      myrcenePercent: 40,
      humulenePercent: 18,
      caryophyllenePercent: 12,
      farnesenePercent: 0.5,
    },
    substitutes: ['columbus', 'zeus', 'apollo', 'warrior'],
    flavorTags: ['tangerine', 'onion', 'garlic', 'dank', 'citrus'],
    description:
      'A dwarf variety with very high alpha and a notorious onion-garlic thiol note that some lots show strongly. Efficient for bittering; taste the lot before you commit it to a dry hop.',
    tags: ['usa', 'yakima', 'bittering', 'high-alpha', 'dwarf'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/summitr',
    provenance: 'spec-sheet',
  },
  {
    id: 'talus',
    name: 'Talus',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 14, max: 17 },
    betaAcidPercent: { min: 5.5, max: 7.5 },
    cohumulonePercentOfAlpha: { min: 26, max: 32 },
    totalOilMlPer100g: { min: 2.5, max: 4.5 },
    oilComposition: {
      myrcenePercent: 45,
      humulenePercent: 17,
      caryophyllenePercent: 10,
      farnesenePercent: 1,
    },
    substitutes: ['sabro', 'mosaic', 'citra'],
    flavorTags: ['grapefruit', 'pink-grapefruit', 'sage', 'dank', 'wood'],
    description:
      'A Sabro seedling from the Hop Breeding Company that trades the coconut for pink grapefruit, sage and a dry, woody dankness. Big oil load suits whirlpool and dry hop in West Coast IPA and dark hoppy beers.',
    tags: ['usa', 'yakima', 'dual-purpose', 'hbc-692', 'proprietary'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/talustm',
    provenance: 'spec-sheet',
  },
  {
    id: 'tangier',
    name: 'Tangier',
    origin: 'United States',
    growingRegion: 'Yakima Valley',
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 9.8, max: 9.8 },
    betaAcidPercent: { min: 7.5, max: 7.5 },
    cohumulonePercentOfAlpha: { min: 34.1, max: 34.1 },
    totalOilMlPer100g: { min: 1.9, max: 1.9 },
    oilComposition: {
      myrcenePercent: 45.34,
      humulenePercent: 27.38,
      caryophyllenePercent: 12.37,
      farnesenePercent: 0.14,
    },
    substitutes: ['cascade', 'centennial', 'amarillo'],
    flavorTags: ['tangerine', 'orange', 'orange-blossom', 'creamsicle'],
    description:
      'Found in an open pollination at Segal Ranch in 2021 and trialled as EXP24-25. It is an orange hop and little else: tangerine and navel orange over creamsicle and orange blossom, with humulene high enough to keep it from turning candied.',
    tags: ['usa', 'yakima', 'segal-ranch', 'citrus'],
    provenance: 'representative',
  },
  {
    id: 'terrasurge-hbc-1325',
    name: 'TerraSurge',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'bittering',
    alphaAcidPercent: { min: 16.4, max: 22.4 },
    betaAcidPercent: { min: 5.8, max: 8.1 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.4, max: 4.5 },
    oilComposition: null,
    substitutes: ['warrior', 'columbus', 'zeus', 'magnum'],
    flavorTags: ['resinous', 'clean'],
    description:
      'HBC 1325, launched commercially in 2026 out of a 2018 cross, and bred for the field as much as the kettle: very high alpha with strong disease resistance behind it. Yakima Chief Ranches publish no aroma description and no oil breakdown yet, and the oil band is wide enough to say the variety is still settling.',
    tags: ['usa', 'hop-breeding-company', 'high-alpha', 'modern'],
    url: 'https://yakimachiefranches.com/create/brands/terrasurge-hbc-1325',
    provenance: 'spec-sheet',
  },
  {
    id: 'triumph',
    name: 'Triumph',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 10, max: 13 },
    betaAcidPercent: { min: 4, max: 6 },
    cohumulonePercentOfAlpha: { min: 22, max: 28 },
    totalOilMlPer100g: { min: 1.5, max: 2.5 },
    oilComposition: null,
    substitutes: ['cascade', 'centennial', 'citra'],
    flavorTags: ['citrus', 'melon', 'floral', 'herbal', 'stone-fruit'],
    description:
      'A newer Hopsteiner release with soft citrus and melon over a light herbal base. Still finding its place; treat it as a clean, moderately intense aroma hop for pale ale and hoppy lager.',
    tags: ['usa', 'hopsteiner', 'dual-purpose', 'modern'],
    url: 'https://www.crosbyhops.com/hops/varieties/triumph',
    provenance: 'representative',
  },
  {
    id: 'us-goldings',
    name: 'US Goldings',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 4, max: 6 },
    betaAcidPercent: { min: 2, max: 3 },
    cohumulonePercentOfAlpha: { min: 25, max: 28 },
    totalOilMlPer100g: { min: 0.4, max: 1 },
    oilComposition: {
      myrcenePercent: 30,
      humulenePercent: 40,
      caryophyllenePercent: 14.5,
      farnesenePercent: null,
    },
    substitutes: ['east-kent-goldings', 'uk-golding', 'sonnet', 'willamette'],
    flavorTags: ['floral', 'honey', 'lemon', 'herbal', 'cardamom'],
    description:
      'Golding stock taken to the Pacific Northwest by way of British Columbia, and grown in Washington and Oregon since the early 1990s. Humulene still leads the oil, so it reads English — honey and cardamom over flowers — with a cleaner lemon edge than Kent gives.',
    tags: ['usa', 'goldings-type', 'english-style', 'aroma'],
    provenance: 'representative',
  },
  {
    id: 'us-northern-brewer',
    name: 'Northern Brewer (US)',
    origin: 'United States',
    growingRegion: 'Oregon',
    cultivar: 'northern-brewer',
    purpose: 'dual',
    alphaAcidPercent: { min: 8, max: 10 },
    betaAcidPercent: { min: 3, max: 5 },
    cohumulonePercentOfAlpha: { min: 20, max: 30 },
    totalOilMlPer100g: { min: 1.5, max: 2 },
    oilComposition: {
      myrcenePercent: 50,
      humulenePercent: 20,
      caryophyllenePercent: 7,
      farnesenePercent: 0.5,
    },
    /** Table I's "Northern Brewer (domestic)". */
    storageStability: {
      alphaLostPercentSixMonths: 20,
      source: 'https://morebeer.com/blogs/articles/hop-storage',
    },
    substitutes: ['northern-brewer', 'chinook', 'columbus', 'nugget'],
    flavorTags: ['woody', 'mint', 'pine', 'earthy', 'resinous'],
    description:
      'The Wye variety grown in the Pacific Northwest: woodier and a shade more minty-pine than the German crop, with enough alpha to bitter a steam beer or California common outright. Keep a little for the late addition if you want the classic mint-wood note.',
    tags: ['usa', 'oregon', 'dual-purpose', 'wye', 'classic'],
    provenance: 'spec-sheet',
  },
  {
    id: 'us-tettnanger',
    name: 'Tettnanger (US)',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 3.5, max: 5.5 },
    betaAcidPercent: { min: 3, max: 5 },
    cohumulonePercentOfAlpha: { min: 20, max: 25 },
    totalOilMlPer100g: { min: 0.5, max: 1 },
    oilComposition: {
      myrcenePercent: 28,
      humulenePercent: 22,
      caryophyllenePercent: 8,
      farnesenePercent: 13,
    },
    /** Table I's "Tettnanger (domestic)". */
    storageStability: {
      alphaLostPercentSixMonths: 42,
      source: 'https://morebeer.com/blogs/articles/hop-storage',
    },
    substitutes: ['tettnanger', 'santiam', 'hallertauer-mittelfrueh', 'liberty'],
    flavorTags: ['floral', 'spicy', 'herbal', 'noble', 'earthy'],
    description:
      'The US-grown Tettnang selection, genetically closer to Fuggle stock than to the German original and a touch earthier for it. Still a fine, low-alpha noble-style aroma hop for lagers, wheat beers and Belgian ales.',
    tags: ['usa', 'aroma', 'noble-type', 'tettnang-derived'],
    provenance: 'spec-sheet',
  },
  {
    id: 'usda-200010-008',
    name: 'USDA 200010-008',
    origin: 'United States',
    growingRegion: 'Oregon',
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 11.1, max: 11.1 },
    betaAcidPercent: { min: 2.8, max: 2.8 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.7, max: 1.7 },
    oilComposition: null,
    substitutes: ['simcoe', 'centennial', 'chinook'],
    flavorTags: ['tangerine', 'peach', 'pine'],
    description:
      'A USDA Elite Line selection with tangerine and peach over fresh pine — the west-coast IPA shape, from a public breeding program rather than a private one. Beta acid is unusually low against its alpha.',
    tags: ['usa', 'oregon', 'usda', 'experimental'],
    provenance: 'representative',
  },
  {
    id: 'vanguard',
    name: 'Vanguard',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 4, max: 6 },
    betaAcidPercent: { min: 5.5, max: 7.5 },
    cohumulonePercentOfAlpha: { min: 14, max: 16 },
    totalOilMlPer100g: { min: 0.9, max: 1.6 },
    oilComposition: {
      myrcenePercent: 35,
      humulenePercent: 35,
      caryophyllenePercent: 10,
      farnesenePercent: 0.5,
    },
    substitutes: ['hallertauer-mittelfrueh', 'mount-hood', 'liberty', 'saaz'],
    flavorTags: ['floral', 'herbal', 'woody', 'noble', 'spicy'],
    description:
      'A USDA Hallertau descendant with very low cohumulone and a humulene-heavy, gently woody aroma. Understated even by noble standards, which makes it ideal for delicate helles and Czech-style pale lagers.',
    tags: ['usa', 'oregon', 'aroma', 'noble-type', 'usda', 'hallertau-derived'],
    url: 'https://hopsteiner.us/variety-data-sheets/vanguard/',
    provenance: 'spec-sheet',
  },
  {
    id: 'vera',
    name: 'Vera',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 5.4, max: 5.4 },
    betaAcidPercent: { min: 4.7, max: 4.7 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.31, max: 1.31 },
    oilComposition: null,
    substitutes: ['cascade', 'centennial', 'citra'],
    flavorTags: ['tropical', 'stone-fruit', 'citrus'],
    description:
      "A USDA release named for Vera Katherine Charles, the mycologist, and bred out of Brewer's Gold, a wild Manitoban and a mildew-resistant American male. Tropical and stone fruit from dry rub through to the glass, at an alpha low enough to use freely.",
    tags: ['usa', 'usda', 'aroma'],
    provenance: 'representative',
  },
  {
    id: 'veterans-blend',
    name: 'Veterans Blend',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 14, max: 14 },
    betaAcidPercent: { min: 0, max: 0 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 0, max: 0 },
    oilComposition: null,
    substitutes: ['citra', 'idaho-7', 'krush'],
    flavorTags: ['stone-fruit', 'tropical', 'woody', 'herbal'],
    description:
      'Selected each year by veterans working in brewing, who also pick the charity the proceeds go to. The eighth annual blend is Krush, Idaho 7, Citra and HBC 1134 at 14 % alpha, with stone fruit and tropical over a woody, herbal base. It changes annually, so the figure belongs to this crop and not to the name; no beta or oil is published.',
    tags: ['usa', 'blend', 'proprietary', 'charity'],
    kind: 'blend',
    provenance: 'representative',
  },
  {
    id: 'vista',
    name: 'Vista',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 9, max: 12 },
    betaAcidPercent: { min: 3.5, max: 5.5 },
    cohumulonePercentOfAlpha: { min: 29, max: 34 },
    totalOilMlPer100g: { min: 1, max: 2 },
    oilComposition: {
      myrcenePercent: 38,
      humulenePercent: 17,
      caryophyllenePercent: 10,
      farnesenePercent: 0.5,
    },
    substitutes: ['cashmere', 'cascade', 'citra', 'mosaic'],
    flavorTags: ['citrus', 'melon', 'tropical', 'pear', 'peach'],
    description:
      'A USDA public hop (ex-USDA 074) from a Perle cross: melon, tangerine, pear and green tea with enough alpha to bitter. Grown in Yakima and in Michigan; a clean modern aroma hop for pale ales, session IPA and hoppy lagers.',
    tags: ['usa', 'usda', 'yakima', 'michigan', 'public', 'dual-purpose', 'modern'],
    url: 'https://www.crosbyhops.com/hops/varieties/vista',
    provenance: 'spec-sheet',
  },
  {
    id: 'warrior',
    name: 'Warrior',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'bittering',
    alphaAcidPercent: { min: 14, max: 17 },
    betaAcidPercent: { min: 4.5, max: 5.5 },
    cohumulonePercentOfAlpha: { min: 22, max: 26 },
    totalOilMlPer100g: { min: 1, max: 2 },
    oilComposition: {
      myrcenePercent: 45,
      humulenePercent: 17,
      caryophyllenePercent: 10,
      farnesenePercent: 0.5,
    },
    substitutes: ['columbus', 'nugget', 'magnum', 'zeus'],
    flavorTags: ['resinous', 'citrus', 'herbal', 'clean'],
    description:
      'High alpha with low cohumulone and a neutral, faintly resinous aroma — as close as the US gets to a clean bittering hop. The default 60-minute addition when you want IBUs and nothing else from the kettle.',
    tags: ['usa', 'yakima', 'bittering', 'high-alpha', 'ycr-5', 'proprietary'],
    url: 'https://yakimachiefranches.com/create/brands/warrior',
    provenance: 'spec-sheet',
  },
  {
    id: 'willamette',
    name: 'Willamette',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 4, max: 6 },
    betaAcidPercent: { min: 3, max: 4.5 },
    cohumulonePercentOfAlpha: { min: 30, max: 35 },
    totalOilMlPer100g: { min: 1, max: 1.6 },
    oilComposition: {
      myrcenePercent: 35,
      humulenePercent: 25,
      caryophyllenePercent: 7,
      farnesenePercent: 6,
    },
    storageStability: {
      alphaLostPercentSixMonths: 37,
      source: 'https://morebeer.com/blogs/articles/hop-storage',
    },
    substitutes: ['fuggle', 'glacier', 'styrian-goldings', 'east-kent-goldings'],
    flavorTags: ['earthy', 'floral', 'herbal', 'blackcurrant', 'spicy'],
    description:
      'The American Fuggle: mild, earthy and floral with a faint blackcurrant note, and for years the most-planted aroma hop in the US. The workhorse late hop for porters, brown ales, ESBs and English-leaning recipes.',
    tags: ['usa', 'oregon', 'aroma', 'english-type', 'fuggle-derived', 'classic'],
    url: 'https://www.crosbyhops.com/hops/varieties/willamette',
    provenance: 'spec-sheet',
  },
  {
    id: 'zappa',
    name: 'Zappa',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 5, max: 8 },
    betaAcidPercent: { min: 5, max: 7 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 1.5, max: 2.5 },
    oilComposition: null,
    substitutes: ['comet', 'cascade', 'el-dorado'],
    flavorTags: ['mango', 'mint', 'spicy', 'tropical', 'herbal'],
    description:
      'A pure neomexicanus variety from CLS Farms with a wild profile: mango and pineapple cut with mint and a peppery, almost savory spice. Distinctive enough to build a beer around, but it does not blend quietly.',
    tags: ['usa', 'yakima', 'aroma', 'neomexicanus', 'cls-farms', 'proprietary'],
    url: 'https://www.crosbyhops.com/hops/varieties/zappa',
    provenance: 'representative',
  },
  {
    id: 'zeus',
    name: 'Zeus',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'bittering',
    alphaAcidPercent: { min: 13, max: 17 },
    betaAcidPercent: { min: 4, max: 6.5 },
    cohumulonePercentOfAlpha: { min: 28, max: 35 },
    totalOilMlPer100g: { min: 2, max: 3 },
    oilComposition: {
      myrcenePercent: 40,
      humulenePercent: 15,
      caryophyllenePercent: 9,
      farnesenePercent: 0.5,
    },
    /** Measured at 22 °C, not 20 °C. */
    storageStability: {
      alphaLostPercentSixMonths: 48,
      source: 'https://patents.google.com/patent/USPP18602P3/en',
    },
    substitutes: ['columbus', 'warrior', 'nugget', 'apollo'],
    flavorTags: ['resinous', 'herbal', 'pungent', 'spicy'],
    description:
      'The third member of the CTZ group, traded alongside Columbus and Tomahawk but a distinct variety with slightly more herbal, less catty pungency. Bought almost entirely for cheap alpha in the kettle.',
    tags: ['usa', 'yakima', 'ctz', 'bittering', 'high-alpha'],
    url: 'https://www.barthhaas.com/hops-and-products/hops/zeus',
    provenance: 'spec-sheet',
  },
  {
    id: 'zumo',
    name: 'Zumo',
    origin: 'United States',
    growingRegion: 'Yakima Valley',
    cultivar: null,
    purpose: 'aroma',
    alphaAcidPercent: { min: 5.5, max: 6.5 },
    betaAcidPercent: { min: 4, max: 5 },
    cohumulonePercentOfAlpha: null,
    totalOilMlPer100g: { min: 0.5, max: 1 },
    oilComposition: null,
    substitutes: ['cascade', 'citra', 'lemondrop'],
    flavorTags: ['lime', 'orange', 'citrus'],
    description:
      'Segal Ranch again, and the name is the whole description: lime zest and orange, clean and juice-like, with nothing herbal or dank behind it. Low oil, so it wants a late whirlpool rather than a long dry hop.',
    tags: ['usa', 'yakima', 'segal-ranch', 'citrus'],
    provenance: 'representative',
  },
  {
    id: 'zythos',
    name: 'Zythos',
    origin: 'United States',
    growingRegion: null,
    cultivar: null,
    purpose: 'dual',
    alphaAcidPercent: { min: 8, max: 12 },
    betaAcidPercent: { min: 5, max: 6.5 },
    cohumulonePercentOfAlpha: { min: 28, max: 32 },
    totalOilMlPer100g: { min: 0.8, max: 1.5 },
    oilComposition: {
      myrcenePercent: 45,
      humulenePercent: 18.5,
      caryophyllenePercent: 8.5,
      farnesenePercent: 2,
    },
    substitutes: ['falconers-flight', 'cascade', 'centennial', 'simcoe', 'columbus'],
    flavorTags: ['tropical', 'pineapple', 'citrus', 'pine', 'herbal'],
    description:
      "Yakima Chief's IPA blend, named for the Greek word for beer and built to sit alongside the C hops rather than replace them. Pineapple and citrus over a light pine and herbal floor, and enough alpha to carry the bittering charge as well as the whirlpool. Like any vendor blend the recipe is the seller's and can move between crops, so the figures describe a typical lot rather than a specification.",
    tags: ['usa', 'blend', 'proprietary', 'pacific-northwest'],
    kind: 'blend',
    provenance: 'representative',
  },
];
